/**
 * Browser-side `CrucibleClient` — JSON-RPC 2.0 over WSS per TDD-v2 §2.3
 * + `docs/cloud-vm/design/jsonrpc-channel.md`.
 *
 * **P0b sub-step 2 (TDD §5.2):** this client is now a thin POLICY layer
 * COMPOSED over the shared, transport-agnostic {@link JsonRpcConnection}
 * core + the browser `WebSocketLike` transport. The framing /
 * request-response correlation / streaming SPSC queue / notification routing
 * all live in the core; this class keeps only the browser-specific policy:
 *
 *   - session lifecycle (`session.hello@1` / `session.resume@1` + resumption
 *     via the core's `inFlightIds()` / retained pending) — sent from the
 *     core's `onOpen` hook so it goes out synchronously on the open event;
 *   - reconnect with exponential backoff + jitter (driven off `onClose`);
 *   - bfcache drain (`rejectAllPending`);
 *   - the channel↔method mapping at the edge + the Electron-only stub
 *     short-circuit + the `game:create` streaming bridge;
 *   - error unification: the core rejects connection-level failures with the
 *     neutral `RpcConnectionError`; the policy maps those to the public
 *     `CrucibleError` (wire-error envelopes already map via `fromJsonRpcError`,
 *     which is the core's injected `mapError`).
 *
 * Its public `CrucibleClient` surface is UNCHANGED from the pre-re-seat class
 * — the 51-case `BrowserCrucibleClient.test.ts` is the behaviour-preservation
 * pin.
 *
 * Session lifecycle:
 *   - First connect: `session.hello@1` → server mints `sessionId`.
 *   - Reconnect: `session.resume@1` with stashed `sessionId` +
 *     `lastSeenSeq` per in-flight request; server replays chunks
 *     when the resumption window hasn't expired.
 *   - bfcache eviction: `drainForBfcache()` rejects every pending
 *     request as retryable, clears resumption state, forces a fresh
 *     `session.hello@1` on the next connect.
 *
 * AbortSignal support: every pending request can be cancelled via
 * `InvokeOptions.signal`; the cancellation fires `request.cancel@1`
 * upstream + rejects the local promise (the core owns the emission).
 *
 * Reconnection: transient closes (codes other than 4401/4402) retry
 * with exponential backoff + ±20% jitter, capped at 5s.
 *
 * Cross-references:
 *   - TDD-v2 §2.3 — class shape
 *   - TDD-v2 §2.5 — error mapping
 *   - crucible-client-sdk-tdd.md §3 / §5.2 — the transport-seam core + re-seat
 *   - jsonrpc-channel.md §3 — session lifecycle + resumption
 *   - jsonrpc-channel.md §4 — method namespacing
 */

import type {
  CreateGameResult,
  DevSessionPairingMetadata,
  InterviewRecoverResult,
  InvokeChannelMap,
  PendingInterviewInteraction,
  PendingInterviewInteractionSnapshot,
  PublishProgress,
  PublishProgressStep,
  PublishResult,
  PushChannelMap,
} from '@volley/crucible-ipc-types';

import { channelToMethod, methodToPush, runtimeServedChannelToMethod } from './channel-router.js';
import {
  agentSendChunkToStream,
  isAgentStreamTerminalChunk,
  type AgentSendWireChunk,
} from './agent-send-stream.js';
import { createGameChunkToProgress, type GameCreateChunk } from './game-create-progress.js';
import {
  DEV_START_FIRST_URL_TIMEOUT_MS,
  devStartClosedEvent,
  devStartUrlSwapEvent,
  makeCloudPreviewSession,
  pairingFromChunk,
  previewModeFromChunk,
  previewUrlFromChunk,
  type DevStartWireChunk,
} from './dev-start-stream.js';
import type {
  ClientCapabilities,
  ClientState,
  CrucibleClient,
  InvokeArgs,
  InvokeChannel,
  InvokeOptions,
  PushChannel,
  RuntimeGuiCapabilities,
  StreamingInvocation,
  SystemMethod,
  SystemMethodMap,
  Unsubscribe,
} from './CrucibleClient.js';
import type {
  ObservingClient,
  ObserveParams,
  ObserveResult,
  ObserverChunkHandler,
  ObserverLifecycleEvent,
  ObserverLifecycleHandler,
  ObserverLifecycleType,
} from './ObservingClient.js';
import type { AuthSource } from './AuthSource.js';
import type { AuthRejectedHandler } from './AuthLifecycle.js';
import {
  JsonRpcConnection,
  RpcConnectionError,
  type AuthAttachment,
} from '@volley/crucible-jsonrpc-core';
import {
  type BrowserWebSocketCtor,
  createBrowserWebSocketTransport,
} from '@volley/crucible-jsonrpc-core/browser';
import { CrucibleError, fromJsonRpcError, requiredCloudRuntimeError } from './error-mapping.js';
import type { ElectronOnlyStubs } from './stub-channels.js';

interface RuntimeHelloResult {
  sessionId?: string;
  supportedMethodVersions?: Record<string, number[]>;
  guiCapabilities?: RuntimeGuiCapabilities;
}

/** Bounded rollout surface. Diagnostic only; the server never authorizes from it. */
export type ClientSurface = 'desktop-unified' | 'desktop-legacy' | 'web-cloud' | 'cli' | 'unknown';

interface PublishRunWireChunk {
  chunkKind: 'progress' | 'result';
  phase?: 'build' | 'upload' | 'deploy' | 'validate' | 'done';
  percent?: number;
  message?: string;
  ok?: boolean;
  gameId?: string;
  url?: string;
  error?: string;
}

interface CloudPendingInteractionSnapshotResult {
  readonly ok: true;
  readonly interaction: PendingInterviewInteraction | null;
}

interface CloudInterviewRecoverResult {
  readonly ok: true;
  readonly status: 'active' | 'replayed' | 'started' | 'not-needed';
  readonly runId?: string | number;
  readonly interaction?: PendingInterviewInteraction;
  readonly recoveryToken?: string;
}

const INTERNAL_INTERVIEW_RECOVERY_PROMPT =
  '/build-game Resume the existing interview from its saved Foundry state. Do not repeat completed work.';

function splitVersionedMethod(method: string): { method: string; version: number } | null {
  const match = /^(.*)@(\d+)$/.exec(method);
  if (!match) return null;
  const version = Number.parseInt(match[2]!, 10);
  if (!Number.isFinite(version)) return null;
  return { method: match[1]!, version };
}

function publishPhaseToStep(phase: PublishRunWireChunk['phase']): PublishProgressStep {
  switch (phase) {
    case 'build':
      return 'build-image';
    case 'validate':
      return 'verify';
    case 'done':
      return 'done';
    case 'deploy':
    case 'upload':
    default:
      return 'deploy';
  }
}

function publishRunChunkToProgress(
  gameId: string,
  chunk: PublishRunWireChunk,
): PublishProgress | null {
  if (chunk.chunkKind !== 'progress') return null;
  const step = publishPhaseToStep(chunk.phase);
  const complete = step === 'done' || chunk.percent === 100;
  return {
    gameId,
    step,
    message: chunk.message ?? (complete ? 'Publish complete' : 'Publishing game'),
    complete,
  };
}

function publishRunChunkToResult(chunk: PublishRunWireChunk): PublishResult {
  const success = chunk.ok === true;
  return {
    success,
    ...(chunk.url !== undefined ? { url: chunk.url } : {}),
    ...(!success ? { error: chunk.error ?? 'Publish failed' } : {}),
  };
}

/** Constructor options. */
export interface BrowserCrucibleClientOptions {
  /**
   * Tenant host without scheme, e.g. `'abc12345.foundry.weekend.app'`.
   *
   * Optional in cookie-auth mode (the SPA-colocation deployment per
   * [Bifrost #87](https://github.com/Volley-Inc/bifrost/pull/87)) where
   * the SPA + cap host share an origin — when omitted, `connect()`
   * uses `window.location.host`. In PKCE-mode deployments where the
   * SPA reaches a separate cap-host domain, this field is required.
   */
  tenantHost?: string;
  /** Per-session capability prefix, e.g. `'/<random-32-byte>/'`. */
  capPrefix: string;
  /**
   * Returns a fresh access token. Called once per connect attempt.
   *
   * **Optional.** When provided (PKCE-mode), the client passes the token
   * to the server via the `crucible-auth.<jwt>` WebSocket subprotocol.
   * When omitted (cookie-auth mode), the client opens the WSS upgrade
   * with NO subprotocol — the browser sends the same-origin session
   * cookie automatically and the ALB / cap host extracts identity from
   * its own auth machinery (e.g. `X-Amzn-Oidc-Identity` injected by ALB
   * Cognito).
   *
   * The cookie-auth path skips the entire token-fetch-and-retry branch
   * (CrucibleError CRUCIBLE-110, exponential backoff on IdP outage) —
   * there is no token to fetch, so there is no IdP-outage failure mode
   * to retry. Connect failures in cookie-auth mode surface as ordinary
   * WebSocket-close events (401 from the upgrade, 4401 from the
   * server-side authValidator) and follow the standard reconnect path.
   */
  getAccessToken?: () => Promise<string>;
  /** Stashed `sessionId` from a previous connect, when resuming. */
  sessionId?: string;
  /**
   * Called whenever the server mints (`session.hello@1` response) or
   * confirms (`session.resume@1` response) a session id, OR clears it
   * (empty string) after a hard session loss. The auth module
   * persists this into sessionStorage so a bfcache restore can
   * resume.
   */
  onSessionId: (id: string) => void;
  /**
   * Optional: factory for the underlying WebSocket. Defaults to the
   * global `WebSocket`. Tests inject a mock here.
   *
   * Threaded through to the browser transport's `webSocketCtor` so the
   * core opens its socket via this factory (the contract pin's
   * `MockWebSocket` still drives the connection unchanged).
   */
  webSocketFactory?: (url: string, protocols: string[]) => WebSocket;
  /**
   * Optional: client version string sent in `session.hello@1.params.clientVersion`.
   * Defaults to `'browser-spa-dev'`; production builds inject via vite
   * defines (BG-4.1 follow-up).
   */
  clientVersion?: string;
  /** Entry point announced in hello/resume for low-cardinality telemetry only. */
  clientSurface?: ClientSurface;
  /**
   * Optional: schedule a reconnect after a transient close. Defaults
   * to `setTimeout`; tests inject a synchronous fake.
   */
  scheduleReconnect?: (fn: () => void, ms: number) => void;
  /**
   * Channels that have GRADUATED to a runtime handler and must route
   * over the wire even though the injected {@link stubChannels} table
   * carries a client-side stub for them (lane A 1a per
   * `docs/cloud-vm/design/game-management-methods.md` §5.1 / §1 open
   * question).
   *
   * `invoke` consults this set BEFORE the stub short-circuit: a
   * channel listed here skips its stub and sends a JSON-RPC request.
   * The stub stays in the table as the fallback for clients that do
   * NOT list the channel (browser-dev / PKCE-mode builds with no
   * runtime behind them), so flag-OFF behaviour is unchanged.
   *
   * Cookie-mode boot (`crucible-web/auth-v2/cookie-spa-boot.ts`)
   * passes `new Set(['app:get-feature-flags'])` so the real runtime
   * snapshot (`cloudVmV1: true` + the per-user cohort overlay) reaches
   * the SPA instead of the static stub's identity-less defaults.
   */
  runtimeServedChannels?: ReadonlySet<keyof InvokeChannelMap>;
  /**
   * Budget (ms) for the `dev:start` streaming bridge to receive the first
   * ready `preview-url` chunk before rejecting `invoke('dev:start')` — so a
   * stalled / log-only dev.start@1 stream can't hang the preview overlay
   * forever (Codex P2 on #1948). Defaults to {@link DEV_START_FIRST_URL_TIMEOUT_MS}
   * (180s, mirroring the desktop's FIRST_URL_TIMEOUT_MS). Tests inject a
   * short value.
   */
  devStartFirstUrlTimeoutMs?: number;
  /**
   * App-specific Electron-only channel stubs, INJECTED by the
   * consuming app (crucible-client-sdk-tdd.md §3.5 / §5.2 step 5).
   *
   * The SDK core carries NO knowledge of specific app channels: when
   * this option is omitted (the default), the client has NO stubs and
   * routes EVERY channel over the wire. The cookie-mode SPA boot
   * (`crucible-web/auth-v2/cookie-spa-boot.ts`) injects the canonical
   * `ELECTRON_ONLY_STUBS` table from
   * `crucible-web/auth-v2/electron-only-stubs.ts`, which preserves the
   * exact Electron-only stub behaviour for the browser SPA.
   *
   * `invoke` consults this table (AFTER the {@link runtimeServedChannels}
   * precedence check): a channel present here whose entry is defined
   * returns its typed sentinel synchronously instead of round-tripping
   * to the cap host.
   */
  stubChannels?: Partial<ElectronOnlyStubs>;
  /**
   * D1 (§4.1): the refresh-aware credential provider. When supplied, the client
   * calls `getAuthAttachment()` for every (re)connect (precedence
   * `authSource > getAccessToken > cookie`), and the result becomes the upgrade
   * credential. An acquisition failure takes the same `CRUCIBLE-110 →
   * reconnecting + backoff` path as a `getAccessToken()` failure. Omitted →
   * legacy `getAccessToken`/cookie behaviour, unchanged.
   */
  authSource?: AuthSource;
}

const MAX_BACKOFF_MS = 5000;
const BASE_BACKOFF_MS = 250;
const JITTER_RATIO = 0.4;

/**
 * One observer subscription entry. The `token` is a UNIQUE per-subscription
 * identity that travels WITH the entry when an automatic reconnect re-keys it
 * to a fresh observerId ({@link BrowserCrucibleClient.migrateObserver}). The
 * unsubscribe closure deletes by `token`, never by handler reference — so a
 * stale unsubscribe (whose entry has since been removed) can't delete a NEWER
 * subscription that happens to share the same handler function (Codex r3).
 */
interface ObserverSubscription<H> {
  readonly token: object;
  readonly handler: H;
}

/**
 * One in-flight `connect()` attempt (#1386 TDD §4.1). This object's IDENTITY is
 * the staleness mechanism that replaces the shared `readyResolve`/`readyReject`
 * slot + the `connectGeneration` counter + the `closedExplicitly` latch: every
 * async continuation captures *its* attempt and acts only while that attempt is
 * still the client's `currentAttempt`. There is no shared field for a stale
 * continuation to corrupt.
 *
 *   - `ready` is what `connect()` returns; settled EXACTLY once — `resolve()` on
 *     session welcome/resumed, `reject()` on supersede/close/drain/failure.
 *   - `abort` cancels THIS attempt's in-flight credential acquisition (and a
 *     reconnect timer it owns). Threading the signal INTO acquisition is
 *     deferred — supersede/close already no-op a stale continuation by identity
 *     (TDD §8 Q3).
 *   - `generation` is diagnostic only (TDD §8 Q2); identity, not the number, is
 *     the correctness mechanism.
 */
class ConnectAttempt {
  readonly ready: Promise<void>;
  /** Aborts THIS attempt's in-flight acquisition (+ the timer it scheduled). */
  readonly abort = new AbortController();
  #resolve!: () => void;
  #reject!: (err: Error) => void;
  #settled = false;

  constructor(readonly generation: number) {
    this.ready = new Promise<void>((resolve, reject) => {
      this.#resolve = resolve;
      this.#reject = reject;
    });
    // A reconnect-driven connect() (and a superseded attempt) may never be
    // awaited — swallow so an un-awaited rejection isn't reported as unhandled.
    this.ready.catch(() => undefined);
  }

  resolve(): void {
    if (this.#settled) return;
    this.#settled = true;
    this.#resolve();
  }

  reject(err: Error): void {
    if (this.#settled) return;
    this.#settled = true;
    this.#reject(err);
  }

  get settled(): boolean {
    return this.#settled;
  }
}

export class BrowserCrucibleClient implements ObservingClient {
  static readonly capabilities: ClientCapabilities = {
    defaultProjectPlacement: 'cloud',
    projectPlacementAuthority: 'cloud-runtime',
    hasLocalFilesystem: false,
    hasAutoUpdater: false,
    hasLocalNetwork: false,
    canInvoke: () => false,
    hasRuntimePreview: false,
    canPublishRun: false,
    ingotScaffolderAvailable: false,
    // Preload-less SPA: the native ProtoHub `BrowserView` is
    // unreachable, and `protohub:*` would fall through to the pod
    // (→ "Method not found: protohub.open@1", crucible#2325).
    hasNativeSimulator: false,
  };

  private capabilitiesSnapshot: ClientCapabilities = {
    defaultProjectPlacement: 'cloud',
    projectPlacementAuthority: 'cloud-runtime',
    hasLocalFilesystem: false,
    hasAutoUpdater: false,
    hasLocalNetwork: false,
    canInvoke: (channel) => this.canInvokeRuntimeChannel(channel),
    hasRuntimePreview: false,
    canPublishRun: false,
    ingotScaffolderAvailable: false,
    hasNativeSimulator: false,
  };

  private supportedRuntimeMethods: Readonly<Record<string, readonly number[]>> = {};
  private capabilitySubscribers = new Set<() => void>();
  /**
   * Subscribers to transport RE-establishment (crucible#3011). Fires when a NEW
   * server session comes up on a fresh socket AFTER the first one — a websocket
   * reconnect or bfcache restore — so plain `invoke(...:subscribe)` consumers
   * (the cloud `game-state`/`phase-progress` hooks) can re-issue their subscribe
   * RPC and restart the server-side poll loop the old socket's close tore down.
   * The observer path re-attaches itself via {@link maybeReobserve}; this is the
   * equivalent hook for non-observer subscribers. PERSIST across reconnects.
   */
  private reconnectSubscribers = new Set<() => void>();
  /**
   * Latches true once the FIRST server session is established (hello/resume
   * welcome). Distinguishes the initial connect (no re-subscribe needed — the
   * consumer's own effect just subscribed) from every later re-establishment
   * (the poll loops died with the old socket → consumers must re-subscribe).
   * Never reset: a bfcache/close+reconnect while a consumer stays mounted still
   * needs the re-subscribe, so a later establishment always fires the signal.
   */
  private hasEstablishedSession = false;

  /** The shared transport-seam core; built lazily on the first connect. */
  private core: JsonRpcConnection | null = null;
  /**
   * The auth attachment the browser transport applies on the NEXT socket
   * open. Recomputed per connect attempt (a fresh JWT in PKCE mode), so the
   * core's transport closure reads this live rather than a value frozen at
   * core construction.
   */
  private currentAuth: AuthAttachment = { kind: 'cookie' };
  /**
   * D1 (§4.3): subscribers to the connection-scoped `auth-rejected` lifecycle event,
   * keyed by a unique per-subscription token (idempotent unsubscribe; a stale
   * unsubscribe can't drop a newer subscription). PERSIST across recovery reconnects.
   */
  private authRejectedSubscribers = new Map<object, AuthRejectedHandler>();
  /** WebSocket ctor handed to the browser transport (adapts `webSocketFactory`). */
  private webSocketCtor: BrowserWebSocketCtor;

  private subscribers = new Map<PushChannel, Set<(payload: unknown) => void>>();
  private lastSeenSeq = new Map<string, number>();
  /**
   * D2 observer-chunk subscribers, keyed by `observerId` (mirrors the node
   * `JsonRpcClient`). A `<originatorMethod>.chunk@1` notification carrying BOTH
   * `observerId` AND `requestId` (the originator's, which belongs to ANOTHER
   * connection's stream) is routed here by {@link interceptObserverChunk},
   * registered as the core's pre-demux interceptor BEFORE the push interceptor.
   * One-observer-per-connection bounds this to ≤1 entry, but it is keyed by id
   * so the route-vs-decline contract matches the node pin
   * (`client-observer-subscription.test.ts`) exactly.
   */
  private observerChunkSubscribers = new Map<string, ObserverSubscription<ObserverChunkHandler>>();
  /**
   * D2 observer-lifecycle subscribers, keyed by `observerId`. The
   * `observerId`-only frames (`session.observer.rebound@1` / `gap@1` /
   * `evicted@1`) — which carry no `requestId` and aren't `PushChannel`s — reach
   * these via the core's `on(method)` (they decline the observer-chunk + push
   * interceptors and fall through), plus the client-synthesised
   * `'reobserve-failed'` signal.
   */
  private observerLifecycleSubscribers = new Map<
    string,
    ObserverSubscription<ObserverLifecycleHandler>
  >();
  /**
   * The live observer for this connection, or null. Set by {@link observe};
   * used to (a) ENFORCE one-observer-per-connection (a second `observe()`
   * throws) and (b) re-issue `session.observe@1` on an automatic reconnect (the
   * server deregisters the observer on the old socket's close, so a transient
   * reconnect would otherwise leave the client silently chunk-less). Holds the
   * params to re-issue + the current `observerId`.
   */
  private activeObserve: { params: ObserveParams; observerId: string } | null = null;
  /** Guards {@link maybeReobserve} against overlapping reconnect re-issues. */
  private reobserveInFlight = false;
  /**
   * Reserves the observer slot SYNCHRONOUSLY while a public {@link observe} is
   * awaiting its `session.observe@1` response. Without it, two concurrent
   * `observe()` calls both pass the {@link activeObserve} check (which is set
   * only AFTER the await resolves) and each send a `session.observe@1`,
   * registering/orphaning duplicate server-side observers despite the
   * one-observer-per-connection invariant (Codex r1).
   */
  private observeInFlight = false;
  private sessionId: string | undefined;
  private _state: ClientState = 'closed';
  private backoffAttempt = 0;
  /**
   * Set by `drainForBfcache()` (Codex round-11): after a bfcache drain the
   * session is torn down but the socket is deliberately left open (the browser
   * owns it across bfcache). This flag suppresses ALL session-init sends
   * (`sendHello`/`sendResume`) so a socket that opens AFTER the drain — e.g. one
   * that was still CONNECTING at drain time — can't handshake and resurrect the
   * session state the drain just cleared. The next `connect()` clears it (once
   * it has abandoned the drained socket + opened a fresh one).
   */
  private suppressHandshake = false;
  /**
   * The single live connect attempt (#1386 §4.1), or null when idle / closed /
   * drained. Replaces `readyResolve`/`readyReject` + `connectGeneration` +
   * `closedExplicitly`: socket-lifecycle continuations (open / hello+resume
   * results / close / connect-error / reconnect timer) are scoped to this by
   * IDENTITY (`isCurrent`). Because `supersede` abandons any prior attempt's
   * socket, the live leaf socket is ALWAYS the current attempt's — so a single
   * slot suffices, with no separate socket-owner to track (§4.4).
   */
  private currentAttempt: ConnectAttempt | null = null;
  /** Monotonic source for `ConnectAttempt.generation` (diagnostics only, §8 Q2). */
  private connectGenerationCounter = 0;
  /**
   * Handle for the OWNED default reconnect timer (§4.5), or null. Set only when
   * `opts.scheduleReconnect` is omitted (production); `cancelReconnectTimer()`
   * clears it. An injected `scheduleReconnect` has no handle to clear — its
   * staleness is covered by the attempt-identity no-op in the scheduled callback.
   */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private clientVersion: string;
  /**
   * Injected app-specific Electron-only stub table (TDD §3.5). DEFAULTS
   * to an empty object: a client with no injection has NO stubs and
   * routes every channel over the wire (the core is stub-agnostic).
   */
  private stubChannels: Partial<ElectronOnlyStubs>;

  constructor(private opts: BrowserCrucibleClientOptions) {
    this.sessionId = opts.sessionId;
    this.clientVersion = opts.clientVersion ?? 'browser-spa-dev';
    // DEFAULT EMPTY: a client with no injection has no stubs (the core
    // is now stub-agnostic per TDD §3.5). The cookie-mode SPA injects
    // the canonical table from crucible-web/auth-v2/electron-only-stubs.ts.
    this.stubChannels = opts.stubChannels ?? {};
    // Adapt the `(url, protocols) => WebSocket` factory to the browser
    // transport's `new (url, protocols?) => WebSocket` ctor shape. A function
    // that returns an object is honoured by `new` (the returned object wins),
    // so the injected `MockWebSocket` factory still drives the socket.
    const factory =
      opts.webSocketFactory ??
      ((url: string, protocols: string[]) => new WebSocket(url, protocols));
    this.webSocketCtor = function (url: string, protocols?: string | string[]): WebSocket {
      const list = Array.isArray(protocols) ? protocols : protocols == null ? [] : [protocols];
      return factory(url, list);
    } as unknown as BrowserWebSocketCtor;
  }

  get state(): ClientState {
    return this._state;
  }

  get capabilities(): ClientCapabilities {
    return this.capabilitiesSnapshot;
  }

  /**
   * Registers a listener for capability snapshot changes.
   *
   * Args:
   *   handler: Callback invoked after `this.capabilities` points at the new snapshot.
   *
   * Returns:
   *   An idempotent unsubscribe function.
   *
   * Raises:
   *   None.
   */
  subscribeCapabilities(handler: () => void): Unsubscribe {
    this.capabilitySubscribers.add(handler);
    return () => {
      this.capabilitySubscribers.delete(handler);
    };
  }

  /**
   * Registers a listener for transport RE-establishment (crucible#3011).
   *
   * The handler fires when a NEW server session is established on a fresh socket
   * AFTER the first one (a reconnect / bfcache restore), NOT on the initial
   * connect. Non-observer subscribers (`game-state`/`phase-progress` hooks) use
   * this to re-invoke their `*:subscribe` RPC so the server restarts the poll
   * loop it tore down on the prior socket's `onConnectionClose`.
   *
   * Args:
   *   handler: Callback invoked after each post-initial session establishment.
   *
   * Returns:
   *   An idempotent unsubscribe function.
   *
   * Raises:
   *   None.
   */
  subscribeReconnect(handler: () => void): Unsubscribe {
    this.reconnectSubscribers.add(handler);
    return () => {
      this.reconnectSubscribers.delete(handler);
    };
  }

  /**
   * Called on every successful session establishment (hello welcome / resume).
   * The FIRST latches {@link hasEstablishedSession} without notifying (the
   * consumer's own effect already subscribed); each LATER one is a reconnect, so
   * it fans {@link reconnectSubscribers}. A throwing subscriber must not break
   * fan-out to the others (best-effort, mirroring the push fan-out).
   */
  private markSessionEstablished(): void {
    if (!this.hasEstablishedSession) {
      this.hasEstablishedSession = true;
      return;
    }
    for (const handler of this.reconnectSubscribers) {
      try {
        handler();
      } catch {
        // A throwing reconnect subscriber must not strand the others.
      }
    }
  }

  private canInvokeRuntimeChannel(channel: InvokeChannel): boolean {
    const parsed = splitVersionedMethod(runtimeServedChannelToMethod(channel));
    if (!parsed) return false;
    const versions = this.supportedRuntimeMethods[parsed.method];
    return Array.isArray(versions) && versions.includes(parsed.version);
  }

  /**
   * Applies runtime-advertised method and GUI capability snapshots.
   *
   * Args:
   *   result: The `session.hello@1` result, if the runtime supplied one.
   *
   * Returns:
   *   Nothing; listeners are notified after the new snapshot is visible.
   *
   * Raises:
   *   None.
   */
  private applyRuntimeHelloCapabilities(result: RuntimeHelloResult | undefined): void {
    this.supportedRuntimeMethods =
      result?.supportedMethodVersions && typeof result.supportedMethodVersions === 'object'
        ? result.supportedMethodVersions
        : {};
    const gui = result?.guiCapabilities ?? {};
    this.capabilitiesSnapshot = {
      ...this.capabilitiesSnapshot,
      hasRuntimePreview: gui.hasRuntimePreview === true,
      canPublishRun: gui.canPublishRun === true,
      ingotScaffolderAvailable: gui.ingotScaffolderAvailable === true,
    };
    for (const handler of this.capabilitySubscribers) {
      handler();
    }
  }

  /**
   * Streaming invocation (BG-5.4 / plan-v2 gate 9). Returns a
   * `StreamingInvocation` whose `chunks` AsyncIterable yields every
   * `agent.send.chunk@1`-style notification correlated by
   * `params.requestId`, and whose `result` promise resolves with
   * the final response envelope. The iterable terminates cleanly
   * when the result resolves; on rejection (cancel, drain, transport
   * error), iterating throws the same error so consumers can
   * unify error handling.
   *
   * Re-seated onto `JsonRpcConnection.stream` with
   * `errorTermination:'throw'` (the browser contract — iterating throws on a
   * stream error). The core owns the chunk SPSC queue + the `request.cancel@1`
   * emission; this method layers the channel→method mapping, the
   * `CrucibleError` mapping (via `result`), and `lastSeenSeq` tracking
   * (resumption) on top.
   *
   * Cancellation: `invocation.cancel()` aborts upstream via
   * `request.cancel@1` AND closes the chunk iterable. Equivalent to
   * passing an `AbortController.abort()` through `opts.signal`.
   */
  invokeStreaming<C extends InvokeChannel, ChunkT = unknown>(
    channel: C,
    args: InvokeChannelMap[C][0],
    opts?: InvokeOptions,
  ): StreamingInvocation<InvokeChannelMap[C][1], ChunkT> {
    const core = this.ensureCoreOrThrow();
    // A controller funnels both the caller's `opts.signal` and the returned
    // `cancel()` into ONE abort the core listens on.
    const controller = new AbortController();
    if (opts?.signal) {
      // An ALREADY-aborted caller signal never fires its `abort`
      // listener, so replay it onto the controller synchronously — the
      // core's dispatch then rejects BEFORE sending the frame, matching
      // the bare `request()` path's abort-before-send contract (Codex
      // round-2 P2 on #1780: a pre-aborted `agent:send` must not start
      // a cloud turn).
      if (opts.signal.aborted) {
        controller.abort();
      } else {
        opts.signal.addEventListener('abort', () => controller.abort(), {
          once: true,
        });
      }
    }
    // The chunk-meta callback needs the requestId, which the core mints inside
    // `stream()`. `stream()` returns synchronously (BEFORE any chunk arrives),
    // so capturing the id by reference here is safe — `onChunkMeta` only fires
    // on later notifications, by which time `requestId` is set.
    let requestId = '';
    const stream = core.stream<ChunkT, InvokeChannelMap[C][1]>(
      channelToMethod(channel),
      args as Record<string, unknown> | undefined,
      {
        signal: controller.signal,
        errorTermination: 'throw',
        onChunkMeta: ({ seq }) => {
          if (seq !== undefined && requestId) {
            this.lastSeenSeq.set(requestId, seq);
          }
        },
      },
    );
    requestId = stream.requestId;
    return {
      chunks: this.mapChunkErrors(stream.chunks),
      result: this.mapConnectionError(stream.done),
      cancel: () => controller.abort(),
    };
  }

  /**
   * Lane A 1c (#1314) — drive `game:create` over the streaming wire
   * and bridge chunks to the `game:create:progress` push channel.
   *
   * Consumes the `game.create.chunk@1` stream + the terminal result:
   *   - each chunk → `createGameChunkToProgress` → fan-out to the
   *     existing `game:create:progress` subscribers (so `NewGame.tsx` /
   *     `Dashboard` render progress unchanged);
   *   - the streaming `result` promise resolves the `CreateGameResult`
   *     this method returns (the success navigation target);
   *   - a streaming reject (scaffold failure, or the recoverable
   *     `created-not-catalogued` orphan-guard, which rejects `done`
   *     with a typed error) propagates as a rejected promise so the
   *     "+ New Game" flow's catch surfaces the failure — AND the
   *     `created-not-catalogued` chunk has already been fanned out as a
   *     `game:create:progress` error so the in-drawer log shows it too.
   *
   * The chunk-drain runs concurrently with awaiting `result`; we drain
   * to completion so the recoverable `ok:false` terminal chunk reaches
   * the push channel before the result settles.
   */
  private async invokeGameCreateStreaming(
    args: InvokeChannelMap['game:create'][0],
    opts: InvokeOptions | undefined,
  ): Promise<CreateGameResult> {
    const invocation = this.invokeStreaming<'game:create', GameCreateChunk>(
      'game:create',
      args,
      opts,
    );

    // Fan every chunk out to the existing game:create:progress
    // subscribers. Runs as a background drain; a chunk-iteration throw
    // (transport error / cancel) is observed via the result promise
    // below, so we swallow it here to avoid an unhandled rejection.
    const drain = (async () => {
      for await (const chunk of invocation.chunks) {
        const progress = createGameChunkToProgress(chunk);
        if (progress) {
          this.emitToSubscribers('game:create:progress', progress);
        }
      }
    })().catch(() => {
      /* terminal error surfaces via `result`; chunk-loop close is clean */
    });

    try {
      return await invocation.result;
    } finally {
      // Ensure the drain has flushed any buffered terminal chunk (e.g.
      // the recoverable `created-not-catalogued` error) to the push
      // subscribers before this method settles.
      await drain;
    }
  }

  /**
   * Crucible #1777 — drive `agent:send` over the streaming wire and
   * bridge chunks to the `agent:stream` push channel. The `agent:send`
   * twin of {@link invokeGameCreateStreaming} (Lane A 1c, #1314).
   *
   * The cloud runtime's `agent.send@1` is a STREAMING method (the
   * turn's chunks arrive as request-correlated `agent.send.chunk@1`
   * notifications, then a terminal result), but the shared Workspace
   * renders chat from `agent:stream` PUSH events. A bare invoke would
   * swallow the request-correlated chunks (they never reach a push
   * subscriber), so the turn would complete server-side while the chat
   * hangs at "working…" forever — the #1777 failure.
   *
   * Consumes the `agent.send.chunk@1` stream + the terminal result:
   *   - each originator chunk → `agentSendChunkToStream` → fan-out to
   *     the existing `agent:stream` subscribers (so `Workspace.tsx`'s
   *     chunk handler renders text / tool chips / terminals unchanged);
   *   - every fanned-out chunk is stamped with a GUI-shaped stream id
   *     (`gui-<gameId>-<ts>`) so the Workspace's cross-game
   *     discriminator (`^gui-<gameId>-\d+$`) accepts it — the pod's
   *     own protocol sessionId would be silently dropped;
   *   - the streaming `result` promise resolves the invoke with the
   *     terminal envelope (`{stopReason, tokenCount?}` — the channel
   *     contract is `void`, so callers ignore it);
   *   - if the stream ends WITHOUT a terminal chunk (defensive — e.g.
   *     a pre-chunk cancel resolves `{stopReason:'cancelled'}` with no
   *     `interrupted` chunk), a terminal is synthesised from the
   *     result's `stopReason` so the chat reducer's `isAiTyping` flips
   *     false. Same single-terminal contract as the desktop GUI's
   *     `cloud-agent-dispatcher.ts`.
   *   - a PRE-STREAM reject (no terminal chunk delivered) propagates as
   *     a rejected promise so the Workspace's existing `.catch` →
   *     `ipc/send-failed` path finalises the bubble; NO synthetic error
   *     chunk is emitted on that path (it would double-surface the same
   *     failure). A reject that follows a delivered terminal chunk (the
   *     phase-4 driver emits `agent/error` then rejects the trailing
   *     response) is SWALLOWED — the stream already finalised the turn,
   *     and rethrowing would double-handle the same assistant bubble
   *     (Codex P2 on #1780).
   *
   * The chunk-drain runs concurrently with awaiting `result`; we drain
   * to completion so a buffered terminal chunk reaches the push channel
   * before the invoke settles.
   */
  private async invokeAgentSendStreaming(
    args: InvokeChannelMap['agent:send'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['agent:send'][1]> {
    // GUI-shaped stream id, minted per dispatch — mirrors the desktop
    // main process's `makeAgentStreamSessionId(gameId)`.
    const gameId = typeof args?.gameId === 'string' ? args.gameId : '';
    const streamSessionId = `gui-${gameId}-${Date.now()}`;

    const invocation = this.invokeStreaming<'agent:send', AgentSendWireChunk>(
      'agent:send',
      args,
      opts,
    );

    // Fan every translated chunk out to the existing agent:stream
    // subscribers. Runs as a background drain; a chunk-iteration throw
    // (transport error / cancel) is observed via the result promise
    // below, so we swallow it here to avoid an unhandled rejection.
    let terminalSeen = false;
    let initialAutofireAdmission: 'started' | 'not-needed' | undefined;
    const drain = (async () => {
      for await (const raw of invocation.chunks) {
        if (
          raw.chunkKind === 'autofire_admission' &&
          (raw.dispatchOutcome === 'started' || raw.dispatchOutcome === 'not-needed')
        ) {
          initialAutofireAdmission = raw.dispatchOutcome;
          continue;
        }
        const chunk = agentSendChunkToStream(raw, streamSessionId);
        if (!chunk) continue;
        if (isAgentStreamTerminalChunk(chunk)) terminalSeen = true;
        this.emitToSubscribers('agent:stream', chunk);
      }
    })().catch(() => {
      /* terminal error surfaces via `result`; chunk-loop close is clean */
    });

    // The runtime resolves `{stopReason: 'end_turn'|'cancelled',
    // tokenCount?}`; the CHANNEL contract is `void` (callers ignore the
    // value), so the envelope is read through `unknown`.
    let result: { stopReason?: string; dispatchOutcome?: 'started' | 'not-needed' } | undefined;
    try {
      result = (await invocation.result) as unknown as {
        stopReason?: string;
        dispatchOutcome?: 'started' | 'not-needed';
      };
    } catch (err) {
      // Flush the drain BEFORE consulting `terminalSeen` — the terminal
      // chunk may still be buffered when the rejection lands.
      await drain;
      // Codex P2 (#1780): a mid-stream SDK failure emits an
      // `agent/error` terminal chunk AND rejects the trailing JSON-RPC
      // response. The chunk already finalised the turn on the
      // `agent:stream` surface; rethrowing would make the Workspace
      // ALSO treat it as a pre-stream failure (`ipc/send-failed`) and
      // double-handle the same assistant bubble. Swallow the rejection
      // when a terminal chunk was delivered; rethrow only for genuine
      // pre-stream failures (no terminal seen) so the Workspace's
      // catch path surfaces those.
      if (terminalSeen) {
        return { kind: 'started' } as InvokeChannelMap['agent:send'][1];
      }
      throw err;
    }
    // Ensure the drain has flushed any buffered terminal chunk to the
    // push subscribers before this method settles.
    await drain;
    // Result resolved but the wire carried no terminal chunk — the
    // Workspace would wedge at "working…". Synthesise the terminal the
    // runtime's envelope implies. (On the REJECT path above, the catch
    // either swallowed — terminal already delivered — or rethrew for
    // the Workspace's catch to finalise.)
    const dispatchOutcome = initialAutofireAdmission ?? result?.dispatchOutcome ?? 'started';
    if (!terminalSeen && dispatchOutcome !== 'not-needed') {
      this.emitToSubscribers('agent:stream', {
        type: result?.stopReason === 'cancelled' ? 'interrupted' : 'done',
        sessionId: streamSessionId,
      });
    }
    return { kind: dispatchOutcome } as InvokeChannelMap['agent:send'][1];
  }

  /**
   * Crucible #1945 — drive `dev:start` over the streaming wire and
   * resolve the cookie-mode `CloudPreviewDevSession`. The `dev:start`
   * twin of {@link invokeGameCreateStreaming} (#1314) /
   * {@link invokeAgentSendStreaming} (#1777).
   *
   * The cloud runtime's `dev.start@1` is a STREAMING method: the pod
   * dev-server status + the `preview-url{externalUrl}` signal arrive as
   * request-correlated `dev.start.chunk@1` notifications, the `done`
   * chunk closing the stream. The shared Preview overlay calls
   * `invoke('dev:start')` and awaits a session carrying the iframe URL,
   * then listens on `preview:cloud-update` for restarts / stream-end.
   *
   * Consume sequence (mirrors `cloud-preview-launch.ts`, minus the
   * main-process auth / registry / dev-status concerns the browser
   * doesn't have):
   *   - FIRST ready `preview-url` chunk with a non-empty `externalUrl`
   *     → ADOPT: resolve `{runtime:'cloud', sessionId, gameId, previewUrl}`;
   *   - a ready `preview-url` chunk WITHOUT `externalUrl` (version skew —
   *     crucible#1478 / bifrost#122) → reject LOUD (the pod-local `url`
   *     is unreachable from the browser; adopting it would re-create the
   *     #1932 spinner);
   *   - the stream ENDING before any ready URL → reject with the last
   *     error detail;
   *   - post-adoption, a later DIFFERING `externalUrl` → fan a `url-swap`
   *     to the `preview:cloud-update` subscribers (identical URLs are
   *     deduped — bifrost#124's `--host 0.0.0.0` Local+Network double-emit);
   *   - terminal (stream done / error / transport close) → fan `closed`.
   *
   * The detached consumer keeps running after adoption for the session's
   * lifetime — it is the single owner of the `url-swap` / `closed` pushes.
   */
  private async invokeDevStartStreaming(
    args: InvokeChannelMap['dev:start'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['dev:start'][1]> {
    const gameId = typeof args?.gameId === 'string' ? args.gameId : '';
    // GUI-shaped session id, minted per launch — mirrors the desktop
    // main process's `makeSessionId(gameId)` (`gui-<gameId>-<ts>`).
    const sessionId = `gui-${gameId}-${Date.now()}`;

    const invocation = this.invokeStreaming<'dev:start', DevStartWireChunk>(
      'dev:start',
      args,
      opts,
    );

    let adopted = false;
    let lastPushedUrl: string | null = null;
    // Paired cloud preview (crucible#pairing): captured from the ADOPTED
    // ready chunk and reused for the session reply + every url-swap push
    // so the PairingPanel survives a restart. Classic → both undefined.
    let adoptedPreviewMode: 'paired-server-only' | undefined;
    let adoptedPairing: DevSessionPairingMetadata | undefined;
    let firstReadySettled = false;
    // The url rides the resolution (NOT a closure-mutated outer var) so the
    // adopt path reads a `string`, not a CFA-narrowed `string | null`.
    type FirstReady = { kind: 'ready'; previewUrl: string } | { kind: 'ended' };
    let resolveFirstReady!: (v: FirstReady) => void;
    const firstReady = new Promise<FirstReady>((r) => {
      resolveFirstReady = (v) => {
        if (!firstReadySettled) {
          firstReadySettled = true;
          r(v);
        }
      };
    });
    let streamEndDetail = 'stream completed with no preview-url chunk';
    let skewed = false;
    let closedPushed = false;
    let pairingLostPushed = false;

    // Detached consumer — settles `firstReady` (first ready URL → adopt;
    // skew/stream-end → ended) and drives the post-adoption `url-swap` /
    // terminal `closed` pushes. A chunk-iteration throw (transport error /
    // cancel) settles `firstReady({kind:'ended'})` in the finally; we swallow
    // it here to avoid an unhandled rejection.
    const consumer = (async (): Promise<void> => {
      try {
        for await (const chunk of invocation.chunks) {
          if (chunk.chunkKind === 'error' && typeof chunk.line === 'string') {
            streamEndDetail = chunk.line;
          }
          // Paired cloud tunnel death (crucible#pairing / Bugbot #2918): the
          // pod marks the chunk `errorKind: 'paired-tunnel-exited'` when its
          // cloudflared dies mid-session. Drop the captured pairing metadata
          // (so a later url-swap can't re-carry a dead tunnel URL) and notify
          // the renderer via the SAME `preview:pairing-lost` channel the
          // desktop uses. Fires at most once, only once adopted + paired.
          if (
            chunk.chunkKind === 'error' &&
            chunk.errorKind === 'paired-tunnel-exited' &&
            adopted &&
            adoptedPreviewMode === 'paired-server-only' &&
            !pairingLostPushed
          ) {
            pairingLostPushed = true;
            adoptedPreviewMode = undefined;
            adoptedPairing = undefined;
            this.emitToSubscribers('preview:pairing-lost', { gameId });
            continue;
          }
          if (chunk.chunkKind !== 'preview-url') continue;
          if (chunk.state !== 'ready' && chunk.state !== 'restart') continue;
          const external = previewUrlFromChunk(chunk);
          if (external === null) {
            // Version skew: a ready URL signal with no externalUrl. Fatal
            // pre-adoption (reject loud); a post-adoption skewed restart
            // chunk is ignored — the adopted URL keeps serving (a pod
            // contradicting itself mid-session is a pod bug, not a reason
            // to kill a working iframe).
            if (!adopted) {
              skewed = true;
              streamEndDetail = 'preview-url chunk arrived without externalUrl';
              resolveFirstReady({ kind: 'ended' });
            }
            continue;
          }
          if (!adopted) {
            // First ready URL → ADOPT here, INSIDE the consumer, the
            // moment we resolve `firstReady`. Setting `adopted` only in the
            // caller's post-`await` continuation would leave a window in
            // which the consumer keeps looping while `adopted` is still
            // false, so a `url-swap` / terminal chunk processed in that
            // window would be silently dropped (Codex P1a). `lastPushedUrl`
            // seeds the dedup baseline to the adopted URL (the session
            // reply, NOT a swap).
            adopted = true;
            lastPushedUrl = external;
            // Capture the paired metadata from the SAME chunk that carried
            // the adopted URL, so the session reply + later url-swaps agree.
            adoptedPreviewMode = previewModeFromChunk(chunk);
            adoptedPairing = pairingFromChunk(chunk);
            resolveFirstReady({ kind: 'ready', previewUrl: external });
            continue;
          }
          // Post-adoption restart: dedupe identical-URL swaps (bifrost#124 —
          // `--host 0.0.0.0` Vite prints BOTH Local + Network lines → a
          // spurious same-URL restart chunk at startup, and the env-derived
          // externalUrl never actually changes in Phase A). Only a genuinely
          // different URL reaches the renderer.
          if (external !== lastPushedUrl) {
            lastPushedUrl = external;
            this.emitToSubscribers(
              'preview:cloud-update',
              devStartUrlSwapEvent(gameId, sessionId, external, {
                previewMode: adoptedPreviewMode,
                pairing: adoptedPairing,
              }),
            );
          }
        }
      } catch (err) {
        streamEndDetail = err instanceof Error ? err.message : String(err);
      } finally {
        resolveFirstReady({ kind: 'ended' });
        if (adopted && !closedPushed) {
          closedPushed = true;
          this.emitToSubscribers(
            'preview:cloud-update',
            devStartClosedEvent(gameId, sessionId, 'ended'),
          );
        }
      }
    })();
    // Observe BOTH the detached consumer AND the trailing JSON-RPC response
    // (empty DevStartResult) so a post-adoption stream/transport reject —
    // or a `closed`-push subscriber throw that escaped the consumer's
    // finally — can't surface as an unhandled rejection (Codex P1b). The
    // chunk stream's throw is the load-bearing terminal signal; these are
    // belt-and-braces.
    void consumer.catch(() => undefined);
    void invocation.result.catch(() => undefined);

    // Bound the wait for the first ready URL. A stream that only emits
    // `log` chunks (or stalls before `preview-url` / terminal) would
    // otherwise leave `invoke('dev:start')` pending forever → the preview
    // overlay busy forever, re-introducing the #1932 hang (Codex P2).
    // Mirror the desktop's FIRST_URL_TIMEOUT_MS; overridable per-client for
    // tests via `devStartFirstUrlTimeoutMs`.
    const timeoutMs = this.opts.devStartFirstUrlTimeoutMs ?? DEV_START_FIRST_URL_TIMEOUT_MS;
    let timedOut = false;
    let cancelTimeout: () => void = () => undefined;
    const timeout = new Promise<FirstReady>((resolve) => {
      const t = setTimeout(() => {
        timedOut = true;
        resolve({ kind: 'ended' });
      }, timeoutMs);
      cancelTimeout = () => clearTimeout(t);
    });

    let outcome: FirstReady;
    try {
      outcome = await Promise.race([firstReady, timeout]);
    } finally {
      cancelTimeout();
    }

    if (outcome.kind === 'ended') {
      // Never adopted — cancel the upstream stream and surface the cause.
      invocation.cancel();
      await consumer.catch(() => undefined);
      if (timedOut) {
        throw new Error(
          `The Furnace dev server for "${gameId}" did not report a preview URL within ` +
            `${Math.round(timeoutMs / 1000)}s — the pod may still be installing dependencies. ` +
            `Check the build log and try again.`,
        );
      }
      if (skewed) {
        throw new Error(
          `dev.start@1 for "${gameId}" reported the preview ready without an externalUrl — the pod ` +
            `image predates the externalUrl wire field (crucible#1478) or FOUNDRY_PREVIEW_BASE_URL is ` +
            `missing/rejected on the pod (bifrost#122). The pod-local URL is unreachable from the browser.`,
        );
      }
      throw new Error(
        `The Furnace dev server stream for "${gameId}" ended before the preview was ready: ${streamEndDetail}`,
      );
    }

    // ADOPT happened inside the consumer (Codex P1a) — just resolve with the
    // cloud session shape. The detached consumer keeps running (`url-swap` /
    // `closed`) for the session's lifetime. A paired preview carries the
    // captured previewMode + pairing so the renderer's PairingPanel renders.
    return makeCloudPreviewSession(gameId, sessionId, outcome.previewUrl, {
      previewMode: adoptedPreviewMode,
      pairing: adoptedPairing,
    });
  }

  /**
   * Fan a synthetic push payload out to the channel's subscribers.
   * Used by the `game:create` streaming bridge to deliver translated
   * chunks to the `game:create:progress` listeners the SPA registered
   * via `subscribe`, and by the `agent:send` streaming bridge (#1777)
   * for the `agent:stream` listeners. Mirrors the notification fan-out
   * in the core's push interceptor.
   */
  private emitToSubscribers<E extends PushChannel>(event: E, payload: PushChannelMap[E]): void {
    const subs = this.subscribers.get(event);
    subs?.forEach((h) => {
      try {
        h(payload);
      } catch {
        // A throwing subscriber must NOT break fan-out to the other
        // subscribers, and — for the streaming bridges (`game:create`,
        // `agent:send`, `dev:start`) — must NOT escape the chunk consumer's
        // `for await`, which would stop the stream and (post-adoption,
        // unawaited) surface as an unhandled rejection. Best-effort fan-out:
        // swallow the subscriber error (Codex P1b).
      }
    });
  }

  /** Replay a runtime-owned interaction through the renderer's existing push channel. */
  private emitPendingInterviewInteraction(interaction: PendingInterviewInteraction): void {
    switch (interaction.kind) {
      case 'ask-user-question':
        this.emitToSubscribers('agent:ask-user-question', {
          gameId: interaction.gameId,
          toolUseID: interaction.toolUseID,
          input: interaction.input,
        });
        return;
    }
  }

  private assertInteractionGame(
    requestedGameId: string,
    interaction: PendingInterviewInteraction | undefined | null,
  ): void {
    if (interaction && interaction.gameId !== requestedGameId) {
      throw new Error('Cloud runtime returned an interaction for a different project.');
    }
  }

  private async invokePendingInterviewInteractions(
    args: InvokeChannelMap['interview:get-pending-interactions'][0],
    opts: InvokeOptions | undefined,
  ): Promise<PendingInterviewInteractionSnapshot> {
    const method = runtimeServedChannelToMethod('interview:get-pending-interactions');
    this.assertRequiredCloudRuntimeMethod(method);
    let snapshot: CloudPendingInteractionSnapshotResult;
    try {
      snapshot = await this.sendRequest<CloudPendingInteractionSnapshotResult>(
        method,
        args,
        opts?.signal,
      );
    } catch (error) {
      throw this.translateRequiredCloudRuntimeError(error);
    }
    this.assertInteractionGame(args.gameId, snapshot.interaction);
    if (!snapshot.interaction) return { kind: 'none' };
    this.emitPendingInterviewInteraction(snapshot.interaction);
    return { kind: 'pending', interaction: snapshot.interaction };
  }

  private async invokeInterviewRecovery(
    args: InvokeChannelMap['interview:recover'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InterviewRecoverResult> {
    const method = runtimeServedChannelToMethod('interview:recover');
    this.assertRequiredCloudRuntimeMethod(method);
    let recovered: CloudInterviewRecoverResult;
    try {
      recovered = await this.sendRequest<CloudInterviewRecoverResult>(method, args, opts?.signal);
    } catch (error) {
      throw this.translateRequiredCloudRuntimeError(error);
    }
    this.assertInteractionGame(args.gameId, recovered.interaction);
    if (recovered.interaction) this.emitPendingInterviewInteraction(recovered.interaction);

    if (recovered.recoveryToken) {
      // The reservation is transport authority, not renderer state. Await its
      // consumption so `started` cannot escape before the hidden turn succeeds.
      await this.invokeAgentSendStreaming(
        {
          gameId: args.gameId,
          message: INTERNAL_INTERVIEW_RECOVERY_PROMPT,
          recoveryToken: recovered.recoveryToken,
        } as InvokeChannelMap['agent:send'][0],
        opts,
      );
    }

    if (recovered.status === 'not-needed') return { kind: 'not-needed' };
    return {
      kind: recovered.status,
      ...(recovered.runId !== undefined ? { runId: recovered.runId } : {}),
    };
  }

  private assertRequiredCloudRuntimeMethod(method: string): void {
    const parsed = splitVersionedMethod(method);
    const versions = parsed ? this.supportedRuntimeMethods[parsed.method] : undefined;
    if (!parsed || !Array.isArray(versions) || !versions.includes(parsed.version)) {
      throw requiredCloudRuntimeError();
    }
  }

  private translateRequiredCloudRuntimeError(error: unknown): unknown {
    if (error instanceof CrucibleError && (error.rpcCode === -32601 || error.rpcCode === -32003)) {
      return requiredCloudRuntimeError(error);
    }
    return error;
  }

  /**
   * Lane A 1b (#1296) — `app:get-vm-diagnostics` wire-shape adapter.
   *
   * The runtime handler (`app.get-vm-diagnostics@1`) ALWAYS returns a
   * populated object because the JSON-RPC wire encoder rejects bare
   * `null`. The channel contract is `VmDiagnostics | null`, and the
   * `CapCohortStatePanel` consumer treats `null` as the cloud-VM-flag-OFF
   * placeholder. So this adapter maps the flag-OFF wire shape
   * (`routingDecision: 'local'`, the value the runtime reports when the
   * `CRUCIBLE_CLOUD_VM_V1` flag is off) → `null`; a real serving pod
   * reports `'cloud'` (or `'engineering-flag'`) and the object is
   * returned verbatim.
   */
  private async invokeVmDiagnostics(
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['app:get-vm-diagnostics'][1]> {
    const result = await this.sendRequest<
      NonNullable<InvokeChannelMap['app:get-vm-diagnostics'][1]> & {
        routingDecision?: string;
      }
    >(channelToMethod('app:get-vm-diagnostics'), undefined, opts?.signal);
    // Flag-OFF wire shape → null (the disabled-state placeholder the
    // panel renders). A defensive null-guard too: the wire can't encode
    // bare null, but if a future runtime ever does, honour it.
    if (!result || result.routingDecision === 'local') {
      return null;
    }
    return result;
  }

  /**
   * Lane A 1b (#1296) — `app:get-diagnostics` wire-shape adapter.
   *
   * The runtime handler (`app.get-diagnostics@1`) wraps the paste-able
   * diagnostics blob in the legacy `{ diagnostics }` envelope. The channel
   * contract is a bare `string` (Settings feeds it to
   * `scanForSecrets(text)`), so this adapter unwraps `{ diagnostics }` →
   * `string`.
   */
  private async invokeGetDiagnostics(
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['app:get-diagnostics'][1]> {
    const result = await this.sendRequest<{ diagnostics: string }>(
      channelToMethod('app:get-diagnostics'),
      undefined,
      opts?.signal,
    );
    return result.diagnostics;
  }

  /**
   * Cloud Design build handoff — nullable interview state adapter.
   *
   * `build-game:get-interview-state` returns `InterviewState | null` at the IPC
   * boundary. Direct top-level `null` stays invalid on the JSON-RPC success
   * wire, so the runtime returns `{ state }` and the browser unwraps it when the
   * channel is explicitly runtime-served.
   */
  private async invokeBuildGameInterviewState(
    args: InvokeChannelMap['build-game:get-interview-state'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['build-game:get-interview-state'][1]> {
    const result = await this.sendRequest<{
      state: InvokeChannelMap['build-game:get-interview-state'][1];
    }>(channelToMethod('build-game:get-interview-state'), args, opts?.signal);
    return result.state;
  }

  private async invokeBuildGameInterviewStatus(
    args: InvokeChannelMap['build-game:interview-status'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['build-game:interview-status'][1]> {
    const result = await this.sendRequest<{
      status: InvokeChannelMap['build-game:interview-status'][1];
    }>(channelToMethod('build-game:interview-status'), args, opts?.signal);
    return result.status;
  }

  private async invokeConflictsGetPending(
    args: InvokeChannelMap['conflicts:get-pending'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['conflicts:get-pending'][1]> {
    const result = await this.sendRequest<{
      conflicts: InvokeChannelMap['conflicts:get-pending'][1];
    }>(channelToMethod('conflicts:get-pending'), args, opts?.signal);
    return result.conflicts;
  }

  /**
   * crucible#3007 — `art:active` nullable-result adapter. The cloud
   * `art.active@1` handler wraps its `AssetReviewReady | null` result in a
   * one-field `{ active }` envelope (a bare top-level `null` is not a valid
   * JSON-RPC success result — same posture as `invokeBuildGameInterviewState`
   * above). This unwraps it so the channel contract
   * (`AssetReviewReady | null`) is identical for both the desktop Electron
   * IPC path (which has no such restriction) and this cloud path.
   */
  private async invokeArtActive(
    args: InvokeChannelMap['art:active'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['art:active'][1]> {
    const result = await this.sendRequest<{ active: InvokeChannelMap['art:active'][1] }>(
      channelToMethod('art:active'),
      args,
      opts?.signal,
    );
    return result.active;
  }

  private async invokeGameStatus(
    project: InvokeChannelMap['game:status'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['game:status'][1]> {
    return this.sendRequest<InvokeChannelMap['game:status'][1]>(
      channelToMethod('game:status'),
      { gameId: project.gameId },
      opts?.signal,
    );
  }

  private async invokePublishRunStreaming(
    args: InvokeChannelMap['publish:run'][0],
    opts: InvokeOptions | undefined,
  ): Promise<InvokeChannelMap['publish:run'][1]> {
    const invocation = this.invokeStreaming<'publish:run', PublishRunWireChunk>(
      'publish:run',
      args,
      opts,
    );
    let terminal: PublishResult | undefined;

    // Keep the background chunk drain observed while the final result is pending.
    const drain = (async () => {
      for await (const chunk of invocation.chunks) {
        if (chunk.chunkKind === 'progress') {
          const progress = publishRunChunkToProgress(args.gameId, chunk);
          if (progress) {
            this.emitToSubscribers('publish:progress', progress);
          }
          continue;
        }
        if (chunk.chunkKind === 'result') {
          terminal = publishRunChunkToResult(chunk);
          if (terminal.success === false) {
            this.emitToSubscribers('publish:progress', {
              gameId: args.gameId,
              step: 'failed',
              message: terminal.error ?? 'Publish failed',
              complete: true,
              ...(terminal.error ? { error: terminal.error } : {}),
            });
          }
        }
      }
    })().catch(() => {
      /* terminal error surfaces via `result`; chunk-loop close is clean */
    });

    try {
      await invocation.result;
      await drain;
    } catch (err) {
      await drain.catch(() => undefined);
      if (terminal) return terminal;
      throw err;
    }

    if (!terminal) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: 'publish.run@1 completed without a terminal publish result chunk',
        code: 'CRUCIBLE-502',
        category: 'network',
        retryable: true,
      });
    }
    return terminal;
  }

  async invoke<C extends InvokeChannel>(
    channel: C,
    ...rest: InvokeArgs<C>
  ): Promise<InvokeChannelMap[C][1]> {
    const { args, opts } = unpackInvokeArgs(rest);

    // Lane A 1c (#1314) — `game:create` STREAMING bridge. The cloud
    // runtime's `game.create@1` is a STREAMING method (progress chunks
    // then a terminal result), but the SPA's "+ New Game" call sites use
    // `invoke('game:create')` + a `game:create:progress` push
    // subscription. A bare `invoke` would swallow the request-correlated
    // chunks (they never reach a push subscriber), so progress + the
    // recoverable `created-not-catalogued` terminal would never render.
    //
    // When `game:create` is routed to the runtime (cookie-mode adds it
    // to `runtimeServedChannels`), route it through `invokeStreaming`
    // instead, translate each chunk into the `game:create:progress`
    // push payload, and fan it out to the EXISTING subscribers — zero
    // call-site change. The streaming `result` promise resolves the
    // `CreateGameResult` the flow awaits. In a non-runtime-served build
    // (PKCE / browser-dev) this branch is skipped and `game:create`
    // falls through to its rejecting stub below, unchanged.
    if (channel === 'game:create' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeGameCreateStreaming(
        args as InvokeChannelMap['game:create'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    // Crucible #1777 — `agent:send` STREAMING bridge. The cloud
    // runtime's `agent.send@1` is a STREAMING method (the turn's chunks
    // are request-correlated `agent.send.chunk@1` notifications), but
    // the shared Workspace's chat sends via `invoke('agent:send')` and
    // renders from an `agent:stream` push subscription. A bare invoke
    // would swallow the correlated chunks (they never reach a push
    // subscriber), so the turn completes server-side while the chat
    // hangs at "working…" forever. Same defect class — and same fix
    // shape — as the `game:create` bridge above (#1314).
    //
    // When `agent:send` is routed to the runtime (cookie-mode adds it
    // to `runtimeServedChannels`), route it through `invokeStreaming`
    // instead, translate each originator chunk into the `agent:stream`
    // push payload, and fan it out to the EXISTING subscribers — zero
    // call-site change. In a non-runtime-served build (PKCE /
    // browser-dev) this branch is skipped and `agent:send` keeps its
    // pre-#1777 bare-request wire behaviour, unchanged.
    if (channel === 'agent:send' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeAgentSendStreaming(
        args as InvokeChannelMap['agent:send'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    if (
      channel === 'interview:get-pending-interactions' &&
      this.opts.runtimeServedChannels?.has(channel)
    ) {
      return this.invokePendingInterviewInteractions(
        args as InvokeChannelMap['interview:get-pending-interactions'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    if (channel === 'interview:recover' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeInterviewRecovery(
        args as InvokeChannelMap['interview:recover'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    // Crucible #1945 — `dev:start` STREAMING bridge. The cloud runtime's
    // `dev.start@1` is a STREAMING method (the pod dev-server status +
    // `preview-url{externalUrl}` arrive as request-correlated
    // `dev.start.chunk@1` notifications), but the shared Preview overlay
    // (`usePreviewSession.start()`) calls `invoke('dev:start')` and awaits a
    // `CloudPreviewDevSession`. A bare invoke would swallow the correlated
    // chunks (`stream chunk for unknown requestId — dropping`) and never
    // resolve, so the overlay hangs on its placeholder forever (#1932). Same
    // defect class — and same fix shape — as the `game:create` (#1314) and
    // `agent:send` (#1777) bridges above.
    //
    // When `dev:start` is routed to the runtime (cookie-mode adds it to
    // `runtimeServedChannels`), route it through `invokeStreaming`, resolve
    // the cloud session shape from the first ready `preview-url` chunk, and
    // fan later restarts / stream-end to the existing `preview:cloud-update`
    // subscribers. In a non-runtime-served build (PKCE / browser-dev) this
    // branch is skipped and `dev:start` falls through to its stub / bare wire
    // behaviour, unchanged.
    if (channel === 'dev:start' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeDevStartStreaming(
        args as InvokeChannelMap['dev:start'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    if (channel === 'publish:run' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokePublishRunStreaming(
        args as InvokeChannelMap['publish:run'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }

    if (channel === 'game:status' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeGameStatus(args as InvokeChannelMap['game:status'][0], opts) as Promise<
        InvokeChannelMap[C][1]
      >;
    }

    // Lane A 1b (#1296) — diagnostics wire-shape ADAPTERS. One method maps
    // the runtime's null-sentinel object to `null`; the other unwraps the
    // legacy `{ diagnostics }` envelope. When routed to the runtime
    // (cookie-mode adds them to `runtimeServedChannels`) we send the request
    // and reshape the result to the channel contract.
    // In a non-runtime-served build (PKCE / browser-dev) these fall
    // through to their injected `stubChannels` stubs below, unchanged.
    if (channel === 'app:get-vm-diagnostics' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeVmDiagnostics(opts) as Promise<InvokeChannelMap[C][1]>;
    }
    if (channel === 'app:get-diagnostics' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeGetDiagnostics(opts) as Promise<InvokeChannelMap[C][1]>;
    }
    if (
      channel === 'build-game:get-interview-state' &&
      this.opts.runtimeServedChannels?.has(channel)
    ) {
      return this.invokeBuildGameInterviewState(
        args as InvokeChannelMap['build-game:get-interview-state'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }
    if (
      channel === 'build-game:interview-status' &&
      this.opts.runtimeServedChannels?.has(channel)
    ) {
      return this.invokeBuildGameInterviewStatus(
        args as InvokeChannelMap['build-game:interview-status'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }
    if (channel === 'conflicts:get-pending' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeConflictsGetPending(
        args as InvokeChannelMap['conflicts:get-pending'][0],
        opts,
      ) as Promise<InvokeChannelMap[C][1]>;
    }
    if (channel === 'art:active' && this.opts.runtimeServedChannels?.has(channel)) {
      return this.invokeArtActive(args as InvokeChannelMap['art:active'][0], opts) as Promise<
        InvokeChannelMap[C][1]
      >;
    }

    // Stub-channel short-circuit (plan-v2 gate 13). Electron-only
    // channels return a typed sentinel synchronously without a
    // round-trip — the cap host doesn't speak these methods. The stub
    // table is INJECTED via `opts.stubChannels` (TDD §3.5): a client
    // with no injection has NO stubs and falls through to the wire.
    //
    // EXCEPT channels that have graduated to a runtime handler (lane A
    // 1a): if the caller marked this channel `runtimeServedChannels`,
    // skip the stub so it routes over the wire to the real method.
    if (channel in this.stubChannels && !this.opts.runtimeServedChannels?.has(channel)) {
      const stub = this.stubChannels[channel];
      if (stub) {
        // Cast: stub's signature is generic across InvokeChannelMap;
        // the `channel in this.stubChannels` guard above plus the
        // ElectronOnlyStubs type bound guarantee the args/return shape.
        return (stub as (a: InvokeChannelMap[C][0]) => Promise<InvokeChannelMap[C][1]>)(
          args as InvokeChannelMap[C][0],
        );
      }
    }

    if (this.opts.runtimeServedChannels?.has(channel) && !isRuntimeServedParamsEnvelope(args)) {
      throw new TypeError(
        `Runtime-served channel ${String(channel)} must use object JSON-RPC params`,
      );
    }

    const method = this.opts.runtimeServedChannels?.has(channel)
      ? runtimeServedChannelToMethod(channel)
      : channelToMethod(channel);
    return this.sendRequest<InvokeChannelMap[C][1]>(method, args, opts?.signal);
  }

  subscribe<E extends PushChannel>(
    event: E,
    handler: (payload: PushChannelMap[E]) => void,
  ): Unsubscribe {
    let set = this.subscribers.get(event);
    if (!set) {
      set = new Set();
      this.subscribers.set(event, set);
    }
    const wrapped = handler as (p: unknown) => void;
    set.add(wrapped);
    return () => {
      set?.delete(wrapped);
    };
  }

  async system<M extends SystemMethod>(
    method: M,
    params: SystemMethodMap[M][0],
  ): Promise<SystemMethodMap[M][1]> {
    return this.sendRequest<SystemMethodMap[M][1]>(method, params);
  }

  // ── D2: the browser observe path (P3 §4.2) ──────────────────────────────

  /**
   * Begin observing — a typed `session.observe@1` request. Enforces
   * one-observer-per-connection: a second `observe()` with a live observer
   * throws (the server tears observer state down only on socket close, so a
   * second observer on one connection would orphan the first's fan-out queues;
   * `close()` + reconnect to swap).
   */
  async observe(params: ObserveParams): Promise<ObserveResult> {
    // Reserve the slot SYNCHRONOUSLY (before any await): a second observe()
    // racing the first's in-flight response must not also pass this check and
    // register a duplicate server-side observer (Codex r1). `observeInFlight`
    // covers the pending window; `activeObserve` covers the resolved one.
    if (this.activeObserve || this.observeInFlight) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message:
          'observe() called with a live observer — one observer per connection (close() + reconnect to swap)',
        code: 'CRUCIBLE-499',
        category: 'usage',
        retryable: false,
      });
    }
    this.observeInFlight = true;
    try {
      const result = await this.sendObserveRequest(params);
      this.activeObserve = { params, observerId: result.observerId };
      return result;
    } finally {
      this.observeInFlight = false;
    }
  }

  /**
   * Route observer chunk notifications (dual-key `observerId` + `requestId`) to
   * `handler` over the core's pre-demux interceptor. Mirrors the node
   * `JsonRpcClient.subscribeObserverChunks` contract. Throws on a duplicate
   * `observerId`.
   */
  subscribeObserverChunks(observerId: string, handler: ObserverChunkHandler): Unsubscribe {
    if (this.observerChunkSubscribers.has(observerId)) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: `subscribeObserverChunks: observerId already subscribed: ${observerId}`,
        code: 'CRUCIBLE-499',
        category: 'usage',
        retryable: false,
      });
    }
    const token = {};
    this.observerChunkSubscribers.set(observerId, { token, handler });
    // Delete by the unique per-subscription TOKEN (which migrates with the entry
    // on a reconnect re-key), guarded by a per-closure `done` flag so a repeated
    // or stale unsubscribe is a no-op and can never remove a newer subscription
    // sharing the same handler (Codex r3).
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.deleteByToken(this.observerChunkSubscribers, token);
    };
  }

  /**
   * Subscribe to the `observerId`-only lifecycle frames (`rebound`/`gap`/
   * `evicted`) + the client-synthesised `'reobserve-failed'` signal. Throws on a
   * duplicate `observerId`.
   */
  onObserverLifecycle(observerId: string, handler: ObserverLifecycleHandler): Unsubscribe {
    if (this.observerLifecycleSubscribers.has(observerId)) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: `onObserverLifecycle: observerId already subscribed: ${observerId}`,
        code: 'CRUCIBLE-499',
        category: 'usage',
        retryable: false,
      });
    }
    const token = {};
    this.observerLifecycleSubscribers.set(observerId, { token, handler });
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.deleteByToken(this.observerLifecycleSubscribers, token);
    };
  }

  /**
   * Send a `stream.ack@1` watermark — wire params `{requestId, ackSeq}` (the
   * server infers the observer from the connection). Best-effort: a closed
   * socket is a no-op (late acks must not error, jsonrpc-channel.md §3.4).
   */
  ackObserver(requestId: string, ackSeq: number): void {
    const core = this.core;
    if (!core) return; // not connected — nothing to ack
    try {
      core.notify('stream.ack@1', { requestId, ackSeq });
    } catch {
      // Socket gone mid-send — the watermark is best-effort; swallow.
    }
  }

  /**
   * D1 (§4.3, {@link AuthLifecycle}): subscribe to the connection-scoped
   * `auth-rejected` event — fired once per terminal `4401`/`4402` close, AFTER the
   * connection's complete teardown (so a synchronous recovery `connect()` starts
   * from a clean slate). Returns an unsubscribe fn; the subscription PERSISTS across
   * recovery reconnects (distinct from the per-observer lifecycle subscribers).
   *
   * Fire-and-forget: the handler's return value is ignored. An `async` handler doing
   * recovery (`await refresh; await connect()`) MUST catch its own failures — the SDK
   * neither awaits nor suppresses them (F13/F17). Recovery (`connect()`) must be
   * driven by a SINGLE owner; passive subscribers must not reconnect (F18).
   */
  onAuthRejected(handler: AuthRejectedHandler): Unsubscribe {
    const token = {};
    this.authRejectedSubscribers.set(token, handler);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.authRejectedSubscribers.delete(token);
    };
  }

  /**
   * Notify `auth-rejected` subscribers — called LAST in the terminal branch, after
   * teardown + `_state='closed'` (F12). Each handler runs in a try/catch so a
   * SYNCHRONOUS throw can't abort teardown or starve other subscribers (F11); the
   * return value is ignored (F13). Iterates a snapshot so a handler that
   * unsubscribes (or subscribes) during dispatch can't disturb the loop.
   */
  private dispatchAuthRejected(closeCode: number): void {
    for (const handler of [...this.authRejectedSubscribers.values()]) {
      try {
        handler({ closeCode });
      } catch {
        // A throwing subscriber must not break teardown or other subscribers.
      }
    }
  }

  connect(): Promise<void> {
    // Resolve the cap host up front. Cookie-auth mode (tenantHost omitted) uses
    // the SPA's own origin (Bifrost #87 colocation). A non-browser env with
    // neither tenantHost nor `window.location` is a non-retryable usage error
    // (CRUCIBLE-114) — surface it as a rejected promise WITHOUT creating or
    // superseding an attempt or touching state (matching the pre-refactor async
    // connect(), which threw before its supersede/state logic).
    let host: string;
    try {
      host = this.resolveHost();
    } catch (err) {
      return Promise.reject(err);
    }

    // #1386 §4.2 — one connect() == one ConnectAttempt. Supersede whatever was
    // current (null after a close()), making THIS the live attempt; a manual
    // connect() also pre-empts any pending backoff timer. `connect()` returns
    // `attempt.ready` SYNCHRONOUSLY; the async `#runAttempt` only ever SETTLES
    // it (never the body the caller awaits — Codex r2 P2-1), so a
    // supersede/close/failure mid-acquisition surfaces to the caller as a
    // rejection, not a false `undefined` success.
    const attempt = new ConnectAttempt(++this.connectGenerationCounter);
    this.supersede(this.currentAttempt, 'connect() superseded by a new connect()');
    // Abandon any live socket up front — including a bfcache-DRAINED socket the
    // core still has installed (drainForBfcache nulls the attempt but deliberately
    // leaves its socket OPEN; the next connect() owns tearing it down — the
    // pre-refactor client did this inside core.connect(), AFTER acquisition). Doing
    // it BEFORE publishing `attempt` preserves the §4.4 invariant — when `attempt`
    // becomes current there is NO live socket attributable to it — so a stale /
    // drained socket's late close can't be misattributed to `attempt` (rejecting
    // its `ready` while it is still acquiring) (Codex r2 P2). RETAINS resumable
    // data-plane (unlike close()'s reject-all). No-op if no socket is live (first
    // connect) or supersede's caller already had none.
    this.core?.abandonSocket('connect() superseded by a new connect()');
    this.currentAttempt = attempt;
    this.cancelReconnectTimer();
    this._state = 'connecting';
    void this.runAttempt(attempt, host);
    return attempt.ready;
  }

  /**
   * The async body of a connect attempt (#1386 §4.2): acquire credentials, then
   * (if still current) open a fresh socket. NEVER what the caller awaits — it
   * only settles `attempt.ready`. Every post-`await` step re-checks
   * `isCurrent(attempt)`: a superseded/closed attempt's continuation returns
   * WITHOUT touching any shared state (can't open a socket, can't write
   * `currentAuth`, can't settle a newer attempt's ready), and the caller still
   * sees the CRUCIBLE-499 that `supersede()` put on `attempt.ready`.
   */
  private async runAttempt(attempt: ConnectAttempt, host: string): Promise<void> {
    // Window #1 — credential acquisition (may await). Precedence
    // authSource > getAccessToken > cookie (D1 §4.2). The named `authSource`
    // seam + the legacy `getAccessToken` hook share the SAME CRUCIBLE-110 →
    // reconnecting + backoff failure path (an IdP/broker outage must not brick
    // the client). Cookie-auth mode (neither supplied) has no credential to
    // fetch, so this branch is bypassed (auth failure surfaces later as a 4401
    // close). Inlined as a SINGLE await (not a helper) to preserve the
    // one-microtask-hop timing the contract suite relies on.
    let acquired: AuthAttachment = { kind: 'cookie' };
    if (this.opts.authSource || this.opts.getAccessToken) {
      try {
        acquired = this.opts.authSource
          ? await this.opts.authSource.getAuthAttachment()
          : {
              kind: 'subprotocol-jwt',
              token: await this.opts.getAccessToken!(),
            };
      } catch (cause) {
        // Superseded/closed during acquisition → attempt.ready is ALREADY
        // rejected (499) by supersede; do nothing (gap #1/#2 by identity).
        if (!this.isCurrent(attempt)) return;
        this.onAcquireFailure(attempt, cause);
        return;
      }
    }
    // Superseded/closed mid-acquire → do NOT open a socket (gap #1/#2). The
    // caller already saw the CRUCIBLE-499 supersede put on attempt.ready.
    if (!this.isCurrent(attempt)) return;

    // Open the socket. A SYNCHRONOUS throw in this section — `ensureCore`
    // (transport/core construction) or the synchronous prefix of
    // `core.connect()` — MUST settle attempt.ready: connect() discards this async
    // body via `void`, so an unsettled ready would otherwise hang the awaiting
    // caller (the pre-refactor inline-async connect() couldn't hang here because a
    // sync throw rejected the same promise the caller awaited — Bugbot). Route a
    // sync throw through the SAME terminal-failure path as an async connect
    // rejection (`onCoreConnectError`'s non-close branch).
    try {
      // The credential resolved above (authSource attachment / PKCE
      // `crucible-auth.<jwt>` subprotocol / cookie). The leaf AuthResolver
      // (`auth: () => this.currentAuth`) reads this live when the core opens the
      // socket.
      this.currentAuth = acquired;
      const core = this.ensureCore(`wss://${host}${this.opts.capPrefix}jsonrpc?v=1`);
      // core.connect() ALWAYS opens a fresh socket with the current auth
      // (abandoning any prior live socket, retaining resumable data-plane
      // pending). The onOpen hook then sends session.hello@1 / session.resume@1
      // for THIS attempt. A close BEFORE open rejects via onCoreConnectError; an
      // after-open close runs through handleClose.
      core.connect().catch((err) => this.onCoreConnectError(attempt, err));
      // Re-enable session negotiation for THIS fresh socket. Cleared only now
      // (not earlier): core.connect() has just abandoned any drained socket — so
      // its late open is ignored by the core's stale-socket guard — while a
      // drained socket that opened during the acquisition above stayed suppressed.
      this.suppressHandshake = false;
    } catch (err) {
      this.onCoreConnectError(attempt, err);
    }
  }

  /**
   * A credential-acquisition failure (authSource/getAccessToken threw). Reject
   * THIS attempt's `ready` with CRUCIBLE-110 (retryable auth) so the awaiting
   * caller sees it (not a shared slot), transition to reconnecting, and schedule
   * the owned backoff timer (cancelled / no-op'd if a manual connect() or
   * close() supersedes the attempt). Only reached while the attempt is current
   * (a close-during-acquisition no-ops earlier, by identity).
   */
  private onAcquireFailure(attempt: ConnectAttempt, cause: unknown): void {
    attempt.reject(
      new CrucibleError(
        {
          name: 'CrucibleError',
          message: 'failed to acquire credential for connect',
          code: 'CRUCIBLE-110',
          category: 'auth',
          retryable: true,
        },
        cause,
      ),
    );
    this._state = 'reconnecting';
    this.scheduleReconnectTimer(() => {
      // Identity no-op (§4.5): only the still-current attempt may reconnect.
      if (!this.isCurrent(attempt)) return;
      // `.catch(() => {})` — the next attempt's own rejection (auth still down →
      // it schedules ANOTHER reconnect) must not become an unhandled rejection.
      this.connect().catch(() => undefined);
    }, this.backoff());
  }

  /**
   * Settle + abort a prior connect attempt (#1386 §4.2). Idempotent via the
   * attempt's settled-once `ready`:
   *   1. settle its `ready` with a typed CRUCIBLE-499 (a caller awaiting the old
   *      connect() sees a clean "superseded", never a hang);
   *   2. abort its in-flight acquisition.
   *
   * The SOCKET teardown is the caller's job, not supersede's: `connect()`
   * abandons the live socket unconditionally (retaining the resumable data-plane,
   * and also catching a bfcache-orphaned socket the prior attempt didn't own —
   * Codex r2 P2), while `close()` lets `core.close()` reject-all + close. This
   * keeps the live-socket teardown in ONE place per path and preserves §4.4 (the
   * live socket always belongs to the current attempt) even across a drain. A
   * no-op when there is no prior attempt (first connect / after close/drain).
   */
  private supersede(prior: ConnectAttempt | null, reason: string): void {
    if (!prior) return;
    prior.reject(
      new CrucibleError({
        name: 'CrucibleError',
        message: reason,
        code: 'CRUCIBLE-499',
        category: 'usage',
        retryable: false,
      }),
    );
    prior.abort.abort();
  }

  private isCurrent(attempt: ConnectAttempt): boolean {
    return this.currentAttempt === attempt;
  }

  async close(): Promise<void> {
    // #1386 §4.3 — close-intent is structural, not a latch: supersede the
    // current attempt (settle its ready 499 + abort its acquisition + abandon
    // its socket) and NULL `currentAttempt`. A close() landing mid-acquisition
    // therefore makes the in-flight attempt non-current, so its acquisition
    // continuation no-ops by identity (gap #1) — there is no `closedExplicitly`
    // boolean for a re-entrant connect() to reset.
    this.supersede(this.currentAttempt, 'client closed before session ready');
    this.currentAttempt = null;
    this.cancelReconnectTimer();
    // D2: tear down the observer NOW (synchronously, before the await) — it
    // belongs to the connection being closed (one-per-connection; the server
    // drops it on socket close). Doing it here, not after core.close(), means a
    // connect() that RACES IN during the await below (the C9 re-open window —
    // which leaves currentAttempt non-null and short-circuits the _state
    // finalization) still starts observer-free, honouring the documented
    // "close() tears down the observer; re-observe explicitly after reconnect"
    // contract (Codex r3 P2). (bfcache drain deliberately does NOT clear it — the
    // socket stays open and the post-pageshow reconnect re-observes via
    // maybeReobserve.)
    this.activeObserve = null;
    this.observeInFlight = false;
    this.reobserveInFlight = false;
    this.observerChunkSubscribers.clear();
    this.observerLifecycleSubscribers.clear();
    // core.close() rejects any remaining in-flight data-plane + control requests
    // (RpcConnectionError → CrucibleError via the request wrappers; the control
    // reject is ignored by onHelloError/onResumeError as local teardown) and is
    // `closedExplicitly` on the core, so it does NOT fire the onClose reconnect
    // path. It also closes the socket (supersede no longer abandons it).
    await this.core?.close();
    // Identity guard (§4.3 / C9): a connect() can race in during the await above
    // and opt back in (sets currentAttempt non-null, _state='connecting'). The
    // _state finalization is the ONLY thing that fresh attempt legitimately owns
    // — don't stomp 'connecting' back to 'closed'. (Observer teardown above is
    // unconditional: the fresh attempt is a NEW connection, observer-free.)
    if (this.currentAttempt !== null) return;
    this._state = 'closed';
  }

  drainForBfcache(): void {
    const error = new CrucibleError({
      name: 'CrucibleError',
      message: 'in-flight request lost across bfcache restore',
      code: 'CRUCIBLE-401',
      category: 'network',
      retryable: true,
    });
    // §4.7 — a connect() in flight is also lost across the bfcache boundary:
    // reject the CURRENT attempt's ready (retryable) so it can't hang, abort its
    // acquisition, and NULL it so a late acquire/open no-ops by identity (the
    // pageshow re-connect is a fresh attempt). Distinct from close(): drain must
    // NOT close the socket (the browser owns it across bfcache) and must NOT set
    // _state='closed'. No-op when no connect is pending (the common
    // drain-after-connect case — the resolved attempt's reject is idempotent).
    const attempt = this.currentAttempt;
    attempt?.reject(error);
    attempt?.abort.abort();
    this.currentAttempt = null;
    this.cancelReconnectTimer();
    // Enter the session-init-suppressed state: the socket is left open (below)
    // but any socket that opens after this drain — incl. one still CONNECTING
    // now — must NOT handshake + resurrect the session we're about to clear
    // (Codex round-11). The next connect() clears this.
    this.suppressHandshake = true;
    // Reject every in-flight DATA-plane request/stream with the bfcache error
    // (streams: chunks throw, done rejects).
    this.core?.rejectAllPending(error);
    // Terminate any in-flight session-init CONTROL request WITHOUT closing the
    // socket. Otherwise the dead bfcache socket could still deliver its
    // hello/resume response and flip state back to 'open' / send a fresh hello
    // AFTER the drain cleared the session (Codex round-9 P2). We must NOT close
    // the socket here: the browser owns the socket's lifecycle across bfcache
    // and the active-client bfcache contract pins that drain does NOT call
    // WebSocket.close() (crucible-web cookie-auth-app-mount.spec.ts — Codex
    // round-10 P2 + the playwright gate). The control request is rejected as a
    // local RpcConnectionError, which onHelloError/onResumeError ignore; the
    // pageshow connect() abandons this socket + opens a fresh one.
    this.core?.rejectControlRequests('session control cancelled across bfcache drain');
    this.lastSeenSeq.clear();
    this.sessionId = undefined;
    this.opts.onSessionId('');
  }

  // ── internals ─────────────────────────────────────────────────────────

  /** Build the core on first connect; reuse it thereafter (it survives reconnects so retained pending can resume). */
  private ensureCore(url: string): JsonRpcConnection {
    if (this.core) return this.core;
    const transport = createBrowserWebSocketTransport({
      webSocketCtor: this.webSocketCtor,
    });
    const core = new JsonRpcConnection({
      url,
      // D1 §4.2: a sync AuthResolver thunk — the leaf calls it fresh inside each
      // `connect()` (before the upgrade), so a per-connect credential refresh
      // reaches the socket. Replaces the prior `auth: <placeholder>` +
      // transport-closure-reads-currentAuth workaround.
      auth: () => this.currentAuth,
      transport,
      mapError: (envelope) => fromJsonRpcError(envelope),
      mintRequestId: () => crypto.randomUUID(),
      onDiagnostic: (message) => console.warn(`[BrowserCrucibleClient] ${message}`),
      // The browser resumption model: keep in-flight requests across a
      // transient close so session.resume@1 can let the server replay them.
      retainPendingOnUnexpectedClose: true,
    });
    core.onOpen(() => this.handleOpen());
    core.onClose((ev) => this.handleClose(ev));
    // D2: the observer-chunk interceptor runs BEFORE the push interceptor
    // (ordering: observer-chunk → push → requestId demux → on(method)). It
    // claims ONLY the dual-key (observerId, requestId) case, so it never steals
    // a push notification; a push notification (no observerId) declines it and
    // reaches the push interceptor next.
    core.registerNotificationInterceptor((method, params) =>
      this.interceptObserverChunk(method, params),
    );
    core.registerNotificationInterceptor((method, params) => this.interceptPush(method, params));
    // D2: the observerId-only lifecycle frames carry no requestId and aren't
    // PushChannels, so they decline both interceptors and fall through to the
    // core's on(method) subscribers — registered here, dispatched by observerId.
    core.on('session.observer.rebound@1', (p) =>
      this.dispatchObserverLifecycle('rebound', 'session.observer.rebound@1', p),
    );
    core.on('session.observer.gap@1', (p) =>
      this.dispatchObserverLifecycle('gap', 'session.observer.gap@1', p),
    );
    core.on('session.observer.evicted@1', (p) =>
      this.dispatchObserverLifecycle('evicted', 'session.observer.evicted@1', p),
    );
    this.core = core;
    return core;
  }

  private ensureCoreOrThrow(): JsonRpcConnection {
    if (!this.core) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: 'transport unavailable: client not connected',
        code: 'CRUCIBLE-401',
        category: 'network',
        retryable: true,
      });
    }
    return this.core;
  }

  private resolveHost(): string {
    if (this.opts.tenantHost) return this.opts.tenantHost;
    if (typeof window !== 'undefined' && window.location?.host) {
      return window.location.host;
    }
    throw new CrucibleError({
      name: 'CrucibleError',
      message:
        'tenantHost not provided and no browser window.location available — pass tenantHost explicitly in non-browser environments',
      code: 'CRUCIBLE-114',
      category: 'usage',
      retryable: false,
    });
  }

  /**
   * Core push-notification interceptor. Runs BEFORE the core's requestId demux.
   * A notification is a PUSH for the SPA iff its method maps to a channel the
   * SPA SUBSCRIBED to — fan it out to the local `subscribe()` handlers (kept
   * here, not in the core, because `subscribe` is keyed by PUSH CHANNEL, not
   * method) and CLAIM it.
   *
   * Everything else returns false and defers to the core: a stream chunk
   * (`*.chunk@1`, whose mapped channel has no subscribers) flows to the core's
   * requestId→stream demux; an unsubscribed / unknown notification is dropped.
   *
   * Crucially this keys off SUBSCRIBED-CHANNEL membership, NOT `requestId`
   * presence: some push payloads (e.g. `vwr:command-output`) carry their OWN
   * `requestId` field that is not a JSON-RPC correlation id, and the
   * pre-re-seat client still delivered those to subscribers (it diverted only a
   * `requestId` that matched a PENDING request). Claiming a delivered push also
   * stops the core from mis-handling such a requestId-bearing push as an
   * unknown stream chunk. (Codex round-2 P2.)
   */
  private interceptPush(method: string, params: Record<string, unknown> | undefined): boolean {
    const pushChannel = methodToPush(method);
    if (!pushChannel) return false;
    const subs = this.subscribers.get(pushChannel);
    if (!subs || subs.size === 0) return false;
    subs.forEach((h) => h(params));
    return true;
  }

  /**
   * D2 core observer-chunk interceptor — runs BEFORE {@link interceptPush}.
   * Mirrors the node `JsonRpcClient`: a notification carrying BOTH a string
   * `observerId` AND a string `requestId` whose `observerId` has a subscriber is
   * delivered + CLAIMED (return `true`). Everything else declines (return
   * `false`): an `observerId` with no subscriber falls through so the core's
   * requestId demux still reports the drop; the `observerId`-only lifecycle
   * frames (no `requestId`) fall through to `on(method)`.
   */
  private interceptObserverChunk(
    method: string,
    params: Record<string, unknown> | undefined,
  ): boolean {
    const observerId = params?.observerId;
    const requestId = params?.requestId;
    if (typeof observerId === 'string' && typeof requestId === 'string') {
      const sub = this.observerChunkSubscribers.get(observerId);
      if (sub) {
        try {
          sub.handler(method, params as Record<string, unknown>);
        } catch {
          /* defensive — a handler throw must not crash dispatch */
        }
        return true;
      }
    }
    return false;
  }

  /** Dispatch an observer lifecycle event to its `observerId` subscriber. */
  private dispatchObserverLifecycle(
    type: ObserverLifecycleType,
    method: string | null,
    params: Record<string, unknown> | undefined,
  ): void {
    const observerId = params?.observerId;
    if (typeof observerId !== 'string') return;
    // `evicted` is TERMINAL server-side — the server has deregistered this
    // observer. Clear the live-observer state (regardless of whether a lifecycle
    // handler is subscribed) BEFORE notifying, so the app can recover by calling
    // observe() again on the STILL-OPEN connection without tripping the
    // one-observer-per-connection guard (Codex r4). `gap`/`rebound` are NOT
    // terminal (the observer keeps running / rebinds), so they don't clear it;
    // `reobserve-failed` + close() are the other terminal clears.
    if (type === 'evicted' && this.activeObserve?.observerId === observerId) {
      this.activeObserve = null;
    }
    const sub = this.observerLifecycleSubscribers.get(observerId);
    if (!sub) return;
    try {
      sub.handler({ type, method, params: params ?? {} });
    } catch {
      /* defensive — a handler throw must not crash dispatch */
    }
  }

  /**
   * Re-establish the observer after an AUTOMATIC reconnect (P3 §4.2, Codex
   * r16/r17). The server deregisters the observer when the old socket closes, so
   * a transient-close reconnect would otherwise leave this client looking
   * connected while silently chunk-less. Detached (fire-and-forget) off the
   * session-ready path so connect() readiness isn't gated on the re-observe.
   *
   * On success the freshly-minted `observerId` differs from the old one, so the
   * existing chunk + lifecycle subscriptions are TRANSPARENTLY migrated to the
   * new id (the app's handlers keep working under the new key — never a silent
   * re-observe under the stale key). On failure the logical observer is torn
   * down and a `'reobserve-failed'` lifecycle event fires so the app can
   * re-observe itself — never a silent dead observer.
   */
  private maybeReobserve(): void {
    const prev = this.activeObserve;
    if (!prev || this.reobserveInFlight) return;
    this.reobserveInFlight = true;
    void (async () => {
      try {
        const result = await this.sendObserveRequest(prev.params);
        if (result.observerId !== prev.observerId) {
          this.migrateObserver(prev.observerId, result.observerId);
        }
        this.activeObserve = { params: prev.params, observerId: result.observerId };
      } catch (err) {
        // Distinguish a re-observe failure where RECOVERY IS PENDING from a
        // TERMINAL one (Codex r5). Recovery is pending when the socket dropped
        // mid-re-observe and the close path has already scheduled another
        // reconnect (`state === 'reconnecting'`), OR a bfcache drain is in
        // progress and the pageshow reconnect will follow (`suppressHandshake`,
        // set by drainForBfcache, which rejects in-flight control requests but
        // keeps state 'open'). In either case KEEP activeObserve so the chained
        // reconnect's maybeReobserve retries — clearing it would strand the
        // observer (the reconnect would skip re-observe → client connected but
        // silently chunk-less). Only when NO recovery is pending (the server
        // REJECTED the observe with the socket still open, or a fully-terminal
        // close) do we tear the logical observer down + signal the app to
        // re-observe (under the OLD id — its handlers are still keyed there).
        // An explicit close() ALSO rejects the in-flight re-observe (via
        // core.close()) — that's intentional teardown, not a failure, so don't
        // emit a spurious reobserve-failed (which could flash recovery UI or
        // trigger an observe() during teardown); close() clears the observer
        // state itself. Codex r6. (#1386: close() AND drain both NULL
        // `currentAttempt`; drain is already caught by `suppressHandshake` above,
        // so `currentAttempt === null` here means a close() tore down — the
        // post-refactor stand-in for the removed `closedExplicitly` latch.)
        const recoveryPending = this._state === 'reconnecting' || this.suppressHandshake;
        if (!recoveryPending && this.currentAttempt !== null) {
          this.activeObserve = null;
          this.dispatchObserverLifecycle('reobserve-failed', null, {
            observerId: prev.observerId,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      } finally {
        this.reobserveInFlight = false;
      }
    })();
  }

  /** Migrate the chunk + lifecycle subscriptions from `oldId` to `newId`. */
  private migrateObserver(oldId: string, newId: string): void {
    const chunk = this.observerChunkSubscribers.get(oldId);
    if (chunk) {
      this.observerChunkSubscribers.delete(oldId);
      this.observerChunkSubscribers.set(newId, chunk);
    }
    const lifecycle = this.observerLifecycleSubscribers.get(oldId);
    if (lifecycle) {
      this.observerLifecycleSubscribers.delete(oldId);
      this.observerLifecycleSubscribers.set(newId, lifecycle);
    }
  }

  /**
   * Delete the map entry whose `token` === `token` — token-keyed unsubscribe,
   * robust to a reconnect re-key (the token migrates with the entry) AND to a
   * re-subscription that reuses the same handler (the new entry carries a
   * DIFFERENT token, so a stale unsubscribe can't remove it). No-op if the
   * token is absent (already removed). (Codex r3.)
   */
  private deleteByToken<H>(map: Map<string, ObserverSubscription<H>>, token: object): void {
    for (const [id, sub] of map) {
      if (sub.token === token) {
        map.delete(id);
        return;
      }
    }
  }

  /**
   * Issue `session.observe@1` on the connection-scoped CONTROL plane (Codex r2
   * P2). It must NOT ride the resumable DATA plane (`core.request`): the browser
   * core retains in-flight data-plane requests across a transient close
   * (`retainPendingOnUnexpectedClose`), but a `session.observe@1` retained that
   * way is never re-registered by `session.resume@1` (the server drops the
   * observer on the old socket's close), so the request would STRAND — the
   * promise / `reobserveInFlight` hangs while no server-side observer exists.
   * The control plane is rejected (not retained) when the socket is
   * replaced/closed, so a mid-observe close surfaces a retryable error the
   * caller (public `observe()`) or the reconnect path (`maybeReobserve`) handles
   * cleanly. The observer is connection-scoped server-side, so control-plane
   * semantics are the correct match.
   */
  private async sendObserveRequest(params: ObserveParams): Promise<ObserveResult> {
    const core = this.core;
    if (!core) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: 'transport unavailable: client not connected',
        code: 'CRUCIBLE-401',
        category: 'network',
        retryable: true,
      });
    }
    try {
      const result = await core.controlRequest<ObserveResult>('session.observe@1', {
        chunkKinds: params.chunkKinds,
        scope: params.scope,
      });
      this.assertObserveResult(result);
      return result;
    } catch (err) {
      throw this.toCrucibleError(err);
    }
  }

  /** Reject a `session.observe@1` reply that lacks the routing-critical `observerId`. */
  private assertObserveResult(result: ObserveResult | undefined): asserts result is ObserveResult {
    if (!result || typeof result.observerId !== 'string') {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: 'session.observe@1 reply missing observerId',
        code: 'CRUCIBLE-401',
        category: 'network',
        retryable: true,
      });
    }
  }

  /** Socket opened → negotiate the session (resume if we have an id, else hello). */
  private handleOpen(): void {
    // The socket that opened belongs to the current attempt — `supersede`
    // abandons a prior attempt's socket, so a stale socket never reaches open
    // here. A null currentAttempt means a drain/close handed the lifecycle on
    // (§4.7): do NOT handshake.
    const attempt = this.currentAttempt;
    if (!attempt) return;
    if (this.sessionId) this.sendResume(attempt);
    else this.sendHello(attempt);
  }

  private sendHello(attempt: ConnectAttempt): void {
    // Drained (bfcache): the session is torn down + the socket intentionally
    // left open — do not handshake (Codex round-11). Cleared by connect().
    if (this.suppressHandshake) return;
    const core = this.core;
    if (!core) return;
    // Control-plane request: NOT in the resumable data-plane pending, so a
    // drain / session-loss / supersede never mistakes it for resumable work,
    // and an abandoned/closed socket rejects it (ignored as local teardown).
    core
      .controlRequest<RuntimeHelloResult>('session.hello@1', {
        clientVersion: this.clientVersion,
        clientSurface: this.opts.clientSurface ?? 'unknown',
        // #2910 — the runtime records + classifies announced client
        // identities per kind; the browser SPA is always 'spa'.
        clientKind: 'spa',
        clientPlatform: 'browser',
        supportedProtocolVersions: [1],
      })
      .then((result) => this.onHelloResult(attempt, result))
      .catch((err) => this.onHelloError(attempt, err));
  }

  private onHelloResult(attempt: ConnectAttempt, result: RuntimeHelloResult | undefined): void {
    if (!this.isCurrent(attempt)) return; // superseded mid-handshake — ignore
    if (!result?.sessionId) {
      attempt.reject(
        new CrucibleError({
          name: 'CrucibleError',
          message: 'session.hello@1 reply missing sessionId',
          code: 'CRUCIBLE-401',
          category: 'network',
          retryable: true,
        }),
      );
      return;
    }
    this.applyRuntimeHelloCapabilities(result);
    this.sessionId = result.sessionId;
    this.opts.onSessionId(this.sessionId);
    this._state = 'open';
    // codex round-1 Q3: reset backoff on session-init success, not raw WS open.
    this.backoffAttempt = 0;
    // §4.5 (gap #3): a successful open pre-empts any pending backoff timer.
    this.cancelReconnectTimer();
    attempt.resolve();
    // D2: re-establish a live observer the server dropped on the prior socket's
    // close (no-op on the first connect — activeObserve is null until observe()).
    this.maybeReobserve();
    // crucible#3011: a fresh hello on a reconnect minted a NEW session — the
    // server tore down the prior socket's game-state/phase-progress poll loops,
    // so notify non-observer subscribers to re-issue their `*:subscribe` RPC.
    // No-op on the first connect (latches instead of firing).
    this.markSessionEstablished();
  }

  private sendResume(attempt: ConnectAttempt): void {
    // Drained (bfcache) — see sendHello (Codex round-11).
    if (this.suppressHandshake) return;
    const core = this.core;
    if (!core || !this.sessionId) return;
    // inFlightIds() is the resumable DATA-plane pending; the resume itself is a
    // control-plane request, so it never appears in its own payload.
    const inflightRequests = core.inFlightIds().map((requestId) => ({
      requestId,
      lastSeenSeq: this.lastSeenSeq.get(requestId) ?? null,
    }));
    core
      .controlRequest<{ kind: 'resumed'; lost?: string[] } | { kind: 'lost' }>('session.resume@1', {
        sessionId: this.sessionId,
        clientSurface: this.opts.clientSurface ?? 'unknown',
        inflightRequests,
      })
      .then((result) => this.onResumeResult(attempt, result))
      .catch((err) => this.onResumeError(attempt, err));
  }

  private onResumeResult(
    attempt: ConnectAttempt,
    result: { kind: 'resumed'; lost?: string[] } | { kind: 'lost' } | undefined,
  ): void {
    if (!this.isCurrent(attempt)) return; // superseded mid-handshake — ignore
    if (result?.kind === 'resumed') {
      // Partial loss: the server couldn't replay these specific requests.
      for (const lostId of result.lost ?? []) {
        this.core?.rejectRequest(lostId, this.sessionLostError());
      }
      this._state = 'open';
      // codex round-1 Q3 (resumed branch): success → reset backoff.
      this.backoffAttempt = 0;
      // §4.5 (gap #3): a successful open pre-empts any pending backoff timer.
      this.cancelReconnectTimer();
      attempt.resolve();
      // D2: a resumed DATA plane does NOT resume the observer (the server drops
      // it on the old socket's close), so re-establish it here too.
      this.maybeReobserve();
      // crucible#3011: even a resumed session rode a NEW socket, so the prior
      // socket's game-state/phase-progress poll loops were torn down on its
      // close — notify non-observer subscribers to re-issue `*:subscribe`. Fires
      // only on a reconnect (a first-connect resume from a stashed sessionId
      // latches without firing).
      this.markSessionEstablished();
      return;
    }
    // kind === 'lost' (or missing) — full session loss → fresh hello.
    this.handleSessionLost(attempt);
  }

  /**
   * INVARIANT (Codex rounds 2–4): a session-init request (hello/resume) lives
   * in the core's pending table, so its rejection has exactly THREE causes,
   * disambiguated by error shape:
   *
   *   1. SERVER wire-error envelope → `CrucibleError` (via the core's
   *      `mapError`). Drives session state — hello fails readiness, resume
   *      falls back to a fresh hello.
   *   2. TRANSPORT send-failure → `RpcConnectionError` kind `transport` (the
   *      socket closed/closing when the frame was sent, or a malformed
   *      response). Fails readiness — connect() can't proceed.
   *   3. LOCAL teardown → `RpcConnectionError` kind `aborted`/`closed` (an abort
   *      from supersede / mid-handshake-close / drain, or `core.close()` from
   *      close()). Ignored HERE: the originating teardown owns readiness +
   *      persisted-session state.
   *
   * Only (1) may drive session state; (3) is ignored; (2) fails readiness.
   * The resume `(1)`-fallback keeps resume working against runtimes that
   * register `session.hello@1` but NOT `session.resume@1` (which method-not-
   * found the resume), matching the pre-re-seat path where a non-`resumed`
   * resume minted a new session.
   */
  private onResumeError(attempt: ConnectAttempt, err: unknown): void {
    if (!this.isCurrent(attempt)) return; // superseded/closed — settled by teardown
    if (this.isLocalTeardown(err)) return; // (3) — settled by the teardown
    if (err instanceof RpcConnectionError) {
      // (2) transport send-failure — a fresh hello would fail to send too.
      this.failReady(attempt, err);
      return;
    }
    // (1) server "not resumable" → fresh hello.
    this.handleSessionLost(attempt);
  }

  private onHelloError(attempt: ConnectAttempt, err: unknown): void {
    if (!this.isCurrent(attempt)) return; // superseded/closed — settled by teardown
    if (this.isLocalTeardown(err)) return; // (3) — settled by the teardown
    // (1) server wire error OR (2) transport send-failure → fail readiness so
    // connect() rejects rather than hanging.
    this.failReady(attempt, err);
  }

  /** A session-init rejection that is LOCAL lifecycle teardown (abort / close), not a server response. */
  private isLocalTeardown(err: unknown): boolean {
    return err instanceof RpcConnectionError && (err.kind === 'aborted' || err.kind === 'closed');
  }

  /** Reject the in-flight attempt's `ready` with the mapped error (idempotent). */
  private failReady(attempt: ConnectAttempt, err: unknown): void {
    attempt.reject(this.toCrucibleError(err));
  }

  /**
   * Full session loss: reject all retained in-flight requests + clear the
   * resumption state + mint a fresh session via `session.hello@1` (its welcome
   * resolves readiness). The resume request itself already settled (removed
   * from pending) before reaching here, so `rejectAllPending` hits only real
   * in-flight work.
   */
  private handleSessionLost(attempt: ConnectAttempt): void {
    this.core?.rejectAllPending(this.sessionLostError());
    this.lastSeenSeq.clear();
    this.sessionId = undefined;
    this.opts.onSessionId('');
    this.sendHello(attempt);
  }

  private sessionLostError(): CrucibleError {
    return new CrucibleError({
      name: 'CrucibleError',
      message: 'session lost outside resumption window',
      code: 'CRUCIBLE-401',
      category: 'network',
      retryable: true,
    });
  }

  private handleClose(ev: { code: number; reason: string }): void {
    // Mid-handshake: settle the in-flight connect()'s `ready` with the SAME
    // typed errors the pre-re-seat client produced. Idempotent — a no-op if the
    // attempt already resolved on welcome (a transient close after open).
    const attempt = this.currentAttempt;
    if (attempt && !attempt.settled) {
      attempt.reject(this.midHandshakeError(ev.code));
    }
    // The unexpected close already rejected any in-flight control request (the
    // session-init) in the core's #onClose; its policy `.catch` ignores that as
    // local teardown. So there's nothing to evict here — just decide reconnect.
    this.maybeReconnect(ev.code);
  }

  /**
   * The core's `connect()` rejects ONLY for a close BEFORE the open event
   * (`RpcConnectionError` carrying the `closeCode`); an after-open close runs
   * through `onClose` instead, so these two paths never both fire for one
   * socket. The core SPLITS them, but the pre-re-seat client handled BOTH in
   * one `onClose` — including scheduling a backoff reconnect for a transient
   * pre-open close (e.g. 1006). So drive the SAME reject-readiness +
   * reconnect decision here off the close code (Codex P1). Identity-guarded
   * so a superseded attempt's late pre-open close can't disturb the current one.
   */
  private onCoreConnectError(attempt: ConnectAttempt, err: unknown): void {
    if (!this.isCurrent(attempt)) return;
    if (err instanceof RpcConnectionError && err.closeCode !== undefined) {
      if (!attempt.settled) {
        attempt.reject(this.midHandshakeError(err.closeCode));
      }
      // A close BEFORE open already rejected any in-flight control request in
      // the core's connect()-onclose path; nothing to evict here.
      this.maybeReconnect(err.closeCode);
      return;
    }
    // A non-close connect failure — e.g. the transport factory THREW because an
    // AuthSource resolved to a browser-incompatible AuthAttachment kind
    // (`header-bearer`/`stub`/`headers`). Map to a typed non-retryable auth
    // CrucibleError (the client's `instanceof CrucibleError` contract — the raw
    // transport Error must not leak), fail readiness, AND transition to a terminal
    // state: this is a config/usage error, not a transient network close, so there
    // is no reconnect — and leaving `_state='connecting'` would brick the client
    // (Codex impl-review F3).
    const mapped =
      err instanceof CrucibleError
        ? err
        : new CrucibleError(
            {
              name: 'CrucibleError',
              message: err instanceof Error ? err.message : 'connect failed',
              code: 'CRUCIBLE-401',
              category: 'auth',
              retryable: false,
            },
            err instanceof Error ? err : undefined,
          );
    // F4: this failure can occur on a RECONNECT — `core.connect()` abandons the
    // old socket and RETAINS data-plane pending (for resumption) *before* the
    // transport throws. Run the complete terminal teardown so those retained
    // invokes/streams reject with `mapped` (not hang), alongside session/observer
    // invalidation + reconnect-timer cancellation.
    this.terminalTeardown(mapped);
    attempt.reject(mapped);
    this._state = 'closed';
  }

  /** The typed error a mid-handshake / pre-open close produces for the pending connect(). */
  private midHandshakeError(code: number): CrucibleError {
    return code === 4401 || code === 4402
      ? new CrucibleError({
          name: 'CrucibleError',
          message: `auth failure (close code ${code})`,
          code: 'CRUCIBLE-401',
          category: 'auth',
          retryable: false,
        })
      : new CrucibleError({
          name: 'CrucibleError',
          message: `socket closed (${code}) before session ready`,
          code: 'CRUCIBLE-401',
          category: 'network',
          retryable: true,
        });
  }

  /**
   * D1 (§4.3): reset ALL connection-scoped state a subsequent caller-driven
   * `connect()` must not inherit, on a TERMINAL (no-reconnect) close. This is the
   * caller-`close()` teardown PLUS `sessionId` invalidation, but it must NOT bar
   * recovery (a caller `connect()` from the `auth-rejected` handler must still
   * re-open) — so it explicitly neutralises session/observer/timer state rather
   * than relying on any close latch (#1386 removed `closedExplicitly`; recovery
   * is gated only by attempt identity) (F22).
   *
   * @param evictionError the close-code-appropriate error for retained data-plane
   *   pending: a non-retryable auth `CrucibleError` for `4401`/`4402` (F8), the
   *   ordinary retryable `closed`/network error for a clean `1000` (F16).
   */
  private terminalTeardown(evictionError: Error): void {
    // (§4.5 / gap #3) a terminal close ends the lifecycle — cancel any pending
    // backoff timer so it can't fire a stale reconnect after teardown.
    this.cancelReconnectTimer();
    // (F3/F8) evict retained data-plane pending with the close-appropriate error,
    // so in-flight invoke()/streams reject promptly instead of hanging on a
    // resumption that will never come.
    this.core?.rejectAllPending(evictionError);
    // (F14/F21) invalidate resume state (in-memory + persisted) so recovery sends a
    // fresh session.hello@1, never session.resume@1 on an auth-dead session.
    this.sessionId = undefined;
    this.lastSeenSeq.clear();
    this.opts.onSessionId('');
    // (F15) tear down D2 observer state (the same clears close() performs) so TV
    // re-pair observe() works and no stale maybeReobserve fires on recovery. The
    // auth-rejected AuthLifecycle subscribers are a DIFFERENT map — they PERSIST.
    this.activeObserve = null;
    this.observeInFlight = false;
    this.reobserveInFlight = false;
    this.observerChunkSubscribers.clear();
    this.observerLifecycleSubscribers.clear();
  }

  /**
   * Transition state + schedule a backoff reconnect for a non-terminal close
   * (matches the pre-re-seat `onClose`): auth closes (4401/4402) + clean closes
   * (1000) + a caller `close()` are terminal; everything else retries with
   * exponential backoff + jitter.
   *
   * D1 (§4.3): a terminal close runs the complete {@link terminalTeardown}; a
   * `4401`/`4402` additionally emits `auth-rejected` ONCE, AFTER teardown +
   * `_state='closed'` (F2/F12). No reconnect on a terminal close — recovery is
   * caller-driven via the `auth-rejected` event.
   */
  private maybeReconnect(code: number): void {
    // No live attempt → a drain handed recovery to the next connect(), or a
    // close() already tore down (and core.close() suppresses onClose, so it
    // normally won't reach here). Either way, do NOT schedule a stale reconnect
    // or fire a recovery event the caller didn't ask for.
    const attempt = this.currentAttempt;
    if (!attempt) {
      this._state = 'closed';
      return;
    }
    if (code === 4401 || code === 4402) {
      this.terminalTeardown(this.midHandshakeError(code)); // auth CRUCIBLE-401 (F8)
      this._state = 'closed';
      this.dispatchAuthRejected(code);
      return;
    }
    if (code === 1000) {
      this.terminalTeardown(this.midHandshakeError(code)); // closed/network error (F16)
      this._state = 'closed';
      return;
    }
    this._state = 'reconnecting';
    this.scheduleReconnectTimer(() => {
      // Identity no-op (§4.5 / gap #3): only the still-current attempt may
      // reconnect — a manual connect()/close()/drain that superseded or nulled
      // it cancels this stale fire, whether or not the timer was cancellable.
      if (!this.isCurrent(attempt)) return;
      this.connect().catch(() => undefined);
    }, this.backoff());
  }

  /** Map a rejected promise's connection-level error to the public CrucibleError. */
  private mapConnectionError<T>(p: Promise<T>): Promise<T> {
    return p.catch((err) => {
      throw this.toCrucibleError(err);
    });
  }

  /**
   * Wrap a core chunk iterable so a connection-level termination throws the
   * public `CrucibleError` (not the core's neutral `RpcConnectionError`) —
   * preserving the browser client's `instanceof CrucibleError` contract for
   * consumers that handle chunk-iteration errors (e.g. a send-on-closed-socket
   * or caller-close mid-stream). A wire-error termination already throws a
   * `CrucibleError` (the core's `mapError`); `toCrucibleError` passes that
   * through unchanged. Codex round-2 P2.
   */
  private mapChunkErrors<T>(chunks: AsyncIterable<T>): AsyncIterable<T> {
    const map = (err: unknown): Error => this.toCrucibleError(err);
    return {
      [Symbol.asyncIterator](): AsyncIterator<T> {
        const inner = chunks[Symbol.asyncIterator]();
        return {
          async next(): Promise<IteratorResult<T>> {
            try {
              return await inner.next();
            } catch (err) {
              throw map(err);
            }
          },
        };
      },
    };
  }

  /**
   * Map a core error to the public `CrucibleError`:
   *   - a wire-error envelope already arrives as `CrucibleError` (the core's
   *     injected `mapError` is `fromJsonRpcError`) → returned unchanged;
   *   - a connection-level `RpcConnectionError` → mapped by `kind`
   *     (abort/superseded → CRUCIBLE-499 usage; transport/closed →
   *     CRUCIBLE-401 retryable network), matching the pre-re-seat contract.
   */
  private toCrucibleError(err: unknown): Error {
    if (err instanceof CrucibleError) return err;
    if (err instanceof RpcConnectionError) {
      switch (err.kind) {
        case 'aborted':
        case 'superseded':
          return new CrucibleError(
            {
              name: 'CrucibleError',
              message: err.message,
              code: 'CRUCIBLE-499',
              category: 'usage',
              retryable: false,
            },
            err,
          );
        case 'transport':
        case 'closed':
          return new CrucibleError(
            {
              name: 'CrucibleError',
              message: err.message,
              code: 'CRUCIBLE-401',
              category: 'network',
              retryable: true,
            },
            err,
          );
      }
    }
    return err instanceof Error ? err : new Error(String(err));
  }

  /** Non-streaming request over the core, mapping connection-level errors to CrucibleError. */
  private async sendRequest<R>(method: string, params: unknown, signal?: AbortSignal): Promise<R> {
    const core = this.core;
    if (!core) {
      throw new CrucibleError({
        name: 'CrucibleError',
        message: 'transport unavailable: client not connected',
        code: 'CRUCIBLE-401',
        category: 'network',
        retryable: true,
      });
    }
    try {
      return await core.request<R>(method, params as Record<string, unknown> | undefined, signal);
    } catch (err) {
      throw this.toCrucibleError(err);
    }
  }

  /**
   * Schedule a reconnect (§4.5). The DEFAULT (production) path owns a real,
   * cancellable timer handle so a manual connect()/close()/terminal-teardown can
   * truly cancel it. An INJECTED `scheduleReconnect` (void return — tests) has
   * no handle to clear; staleness there is covered by the attempt-identity no-op
   * inside the scheduled callback. The option signature is UNCHANGED (§4.5).
   */
  private scheduleReconnectTimer(cb: () => void, ms: number): void {
    if (this.opts.scheduleReconnect) {
      this.opts.scheduleReconnect(cb, ms);
      return;
    }
    this.reconnectTimer = setTimeout(cb, ms);
  }

  /** Cancel the owned default reconnect timer, if any (no-op for an injected one). */
  private cancelReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private backoff(): number {
    const ms = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, this.backoffAttempt++));
    // codex round-1 Q5: clamp AFTER jitter so the returned value
    // never exceeds MAX_BACKOFF_MS.
    const jittered = ms * (1 + (Math.random() - 0.5) * JITTER_RATIO);
    return Math.min(MAX_BACKOFF_MS, jittered);
  }
}

/**
 * Unpack the variadic `InvokeArgs<C>` rest into `{ args, opts }`.
 *
 * Per the widened `InvokeArgs<C>`:
 *   - void-arg channel:       []  |  [opts]
 *   - args-required channel:  [args]  |  [args, opts]
 *
 * The opts slot is detected by shape: an object whose keys are a
 * subset of `INVOKE_OPTION_KEYS` (currently just `signal`). This
 * matches the same detection in `ElectronCrucibleClient.stripOptions`.
 */
function unpackInvokeArgs(rest: readonly unknown[]): {
  args: unknown;
  opts: InvokeOptions | undefined;
} {
  if (rest.length === 0) return { args: undefined, opts: undefined };
  if (rest.length === 1) {
    if (isInvokeOptions(rest[0])) {
      return { args: undefined, opts: rest[0] };
    }
    return { args: rest[0], opts: undefined };
  }
  // [args, opts]
  return {
    args: rest[0],
    opts: isInvokeOptions(rest[1]) ? rest[1] : undefined,
  };
}

const INVOKE_OPTION_KEYS: ReadonlySet<string> = new Set(['signal']);

function isInvokeOptions(value: unknown): value is InvokeOptions {
  if (typeof value !== 'object' || value === null) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  return keys.every((k) => INVOKE_OPTION_KEYS.has(k));
}

function isRuntimeServedParamsEnvelope(value: unknown): boolean {
  return (
    value === undefined || (typeof value === 'object' && value !== null && !Array.isArray(value))
  );
}
